import {
  LocationTypeSchema
} from "../chunk-CLZ4TYDY.js";
import "../chunk-HJPR3SS7.js";
import {
  CreateDeliveryShipmentManifestSchema,
  CreateDeliveryShipmentPartitionAllocationSchema,
  CreateDeliveryShipmentPlanNodeSchema,
  DeliveryShipmentManifestDtoSchema,
  DeliveryShipmentManifestFlowSchema,
  DeliveryShipmentManifestReadinessDtoSchema,
  DeliveryShipmentManifestReadinessIssueSchema,
  DeliveryShipmentManifestReadinessSchema,
  DeliveryShipmentManifestSchema,
  DeliveryShipmentManifestStatusSchema,
  DeliveryShipmentPartitionAllocationDtoSchema,
  DeliveryShipmentPartitionAllocationSchema,
  DeliveryShipmentPartitionAllocationStateSchema,
  DeliveryShipmentPlanNodeDtoSchema,
  DeliveryShipmentPlanNodeSchema,
  DeliveryShipmentPlanNodeTypeSchema,
  HubLayoverScheduleSchema,
  HubNodeMetadataSchema,
  NodeAddressSnapshotSchema,
  ShipmentManifestDestinationSchema,
  ShipmentManifestPackageSchema,
  ShipmentManifestPayloadSnapshotSchema,
  ShipmentManifestShipperSchema,
  ShipmentManifestTransporterDetailSchema,
  ShipmentManifestTransporterSchema,
  UpdateDeliveryShipmentManifestSchema,
  UpdateDeliveryShipmentPartitionAllocationSchema,
  UpdateDeliveryShipmentPlanNodeSchema
} from "../chunk-3XEHZB66.js";

// delivery/webhook.types.ts
var OnfleetWebhookTriggers = {
  TASK_STARTED: 0,
  TASK_ETA: 1,
  TASK_ARRIVAL: 2,
  TASK_COMPLETED: 3,
  TASK_FAILED: 4,
  WORKER_DUTY: 5,
  TASK_CREATED: 6,
  TASK_UPDATED: 7,
  TASK_DELETED: 8,
  TASK_ASSIGNED: 9,
  TASK_UNASSIGNED: 10,
  TASK_CLONED: 11,
  TASK_DELAYED: 12,
  SMS_RECIPIENT_OPT_OUT: 13,
  SMS_RECIPIENT_RESPONSE: 14,
  WORKER_CREATED: 15,
  WORKER_DELETED: 16,
  WORKER_UPDATED: 25,
  AUTO_DISPATCH_JOB_COMPLETED: 24
};

// delivery/order-partition.schema.ts
import { z } from "zod";
var OrderPartitionFulfillmentTypeSchema = z.enum(["brand_driver", "flote_driver", "mail_carrier"]);
var OrderPartitionFulfillmentStatusSchema = z.enum([
  "pending_offer",
  "offer_sent",
  "accepted",
  "declined",
  "expired",
  "in_transit",
  "shipped",
  "assigned",
  "in_progress",
  "completed",
  "cancelled"
]);
var OrderPartitionPreparationStatusSchema = z.enum([
  "not_started",
  "packing",
  "ready_for_delivery",
  "manifest_registered",
  "out_for_delivery",
  "delivered",
  "finalized"
]);
var POST_DEDUCTION_PREPARATION_STATUSES = [
  "manifest_registered",
  "out_for_delivery",
  "delivered",
  "finalized"
];
function isPostDeductionPreparationStatus(status) {
  return POST_DEDUCTION_PREPARATION_STATUSES.includes(status);
}
var OrderPartitionPaymentStatusSchema = z.enum([
  "ach_processing",
  "ach_cleared",
  "ach_transferred",
  "ach_returned",
  "ach_failed",
  "brand_paid",
  "ach_returned_brand_paid",
  "cash_on_delivery_uncollected",
  "cash_on_delivery_collected",
  "cash_on_delivery_brand_paid",
  "money_deposited",
  "delivered_unpaid"
]);
var DbOrderPartitionSchema = z.object({
  id: z.string().uuid(),
  order_id: z.string().uuid().nullable(),
  b2bwave_order_id: z.string(),
  brand_id: z.string().uuid(),
  tracking_id: z.string(),
  display_reference: z.string(),
  portion_subtotal: z.string(),
  // DECIMAL stored as string
  portion_total: z.string(),
  fulfillment_type: OrderPartitionFulfillmentTypeSchema.nullable(),
  fulfillment_status: OrderPartitionFulfillmentStatusSchema,
  preparation_status: OrderPartitionPreparationStatusSchema,
  payment_status: OrderPartitionPaymentStatusSchema.nullable(),
  payment_method: z.string().nullable(),
  stock_location_id: z.string().uuid().nullable(),
  onfleet_task_id: z.string().nullable(),
  // Onfleet task reference
  shipping_carrier: z.string().nullable(),
  // Mail carrier: usps, ups, fedex
  carrier_tracking_number: z.string().nullable(),
  shipped_at: z.union([z.string(), z.date()]).nullable(),
  easypost_tracker_id: z.string().nullable(),
  // EasyPost tracker reference (trk_...)
  spread_rate: z.string().nullable(),
  spread_amount: z.string().nullable(),
  completed_at: z.union([z.string(), z.date()]).nullable(),
  // When marked as completed
  completed_by: z.string().nullable(),
  // Who/what completed it
  has_signature: z.boolean(),
  photo_count: z.number().int().nonnegative(),
  destination_organization_location_id: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()),
  created_at: z.union([z.string(), z.date()]),
  updated_at: z.union([z.string(), z.date()])
});
var OrderPartitionSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid().nullable(),
  b2bwaveOrderId: z.string(),
  brandId: z.string().uuid(),
  trackingId: z.string(),
  displayReference: z.string(),
  portionSubtotal: z.number(),
  portionTotal: z.number(),
  fulfillmentType: OrderPartitionFulfillmentTypeSchema.nullable(),
  fulfillmentStatus: OrderPartitionFulfillmentStatusSchema,
  preparationStatus: OrderPartitionPreparationStatusSchema,
  paymentStatus: OrderPartitionPaymentStatusSchema.nullable(),
  paymentMethod: z.string().nullable(),
  stockLocationId: z.string().uuid().nullable(),
  onfleetTaskId: z.string().nullable(),
  // Onfleet task reference
  shippingCarrier: z.string().nullable(),
  // Mail carrier: usps, ups, fedex
  carrierTrackingNumber: z.string().nullable(),
  shippedAt: z.date().nullable(),
  easypostTrackerId: z.string().nullable(),
  // EasyPost tracker reference (trk_...)
  spreadRate: z.number().nullable(),
  spreadAmount: z.number().nullable(),
  completedAt: z.date().nullable(),
  // When marked as completed
  completedBy: z.string().nullable(),
  // Who/what completed it
  hasSignature: z.boolean().optional(),
  photoCount: z.number().int().nonnegative().optional(),
  destinationOrganizationLocationId: z.string().uuid().nullable(),
  metadata: z.record(z.unknown()),
  createdAt: z.date(),
  updatedAt: z.date()
});
var PendingOfferSummarySchema = z.object({
  id: z.string().uuid(),
  expiresAt: z.string().datetime()
});
var FulfillmentOfferSummarySchema = z.object({
  id: z.string().uuid(),
  status: z.enum(["pending", "accepted", "declined", "expired", "cancelled"]),
  expiresAt: z.string().datetime(),
  respondedAt: z.string().datetime().nullable(),
  respondedByPortalUserId: z.string().uuid().nullable(),
  responseReason: z.string().nullable(),
  createdAt: z.string().datetime()
});
var OrderPartitionDtoSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid().nullable(),
  b2bwaveOrderId: z.string(),
  brandId: z.string().uuid(),
  trackingId: z.string(),
  displayReference: z.string(),
  portionSubtotal: z.number(),
  portionTotal: z.number(),
  fulfillmentType: OrderPartitionFulfillmentTypeSchema.nullable(),
  fulfillmentStatus: OrderPartitionFulfillmentStatusSchema,
  preparationStatus: OrderPartitionPreparationStatusSchema,
  paymentStatus: OrderPartitionPaymentStatusSchema.nullable(),
  paymentMethod: z.string().nullable(),
  stockLocationId: z.string().uuid().nullable(),
  onfleetTaskId: z.string().nullable(),
  // Onfleet task reference
  shippingCarrier: z.string().nullable(),
  // Mail carrier: usps, ups, fedex
  carrierTrackingNumber: z.string().nullable(),
  shippedAt: z.string().datetime().nullable(),
  easypostTrackerId: z.string().nullable(),
  // EasyPost tracker reference (trk_...)
  spreadRate: z.number().nullable(),
  spreadAmount: z.number().nullable(),
  completedAt: z.string().datetime().nullable(),
  // When marked as completed
  completedBy: z.string().nullable(),
  // Who/what completed it
  hasSignature: z.boolean().optional(),
  photoCount: z.number().int().nonnegative().optional(),
  destinationOrganizationLocationId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  customerName: z.string().nullish(),
  // Enriched field: contact name from shipping address
  companyName: z.string().nullish(),
  // Enriched field: company name from customer record
  customerLicenseNumber: z.string().nullish(),
  // Enriched field: active license number from compliance.licenses
  destinationCity: z.string().nullish(),
  // Enriched field: city from the order's shipping address
  totalQuantity: z.number().int().nonnegative().optional(),
  // Sum of all line item quantities
  itemCount: z.number().int().nonnegative().optional(),
  // Count of distinct line items (unique SKUs)
  pendingOffer: PendingOfferSummarySchema.nullish()
  // Present when partition has an open (pending) fulfillment offer
});
var CreateOrderPartitionDtoSchema = z.object({
  orderId: z.string().uuid().optional(),
  b2bwaveOrderId: z.string(),
  brandId: z.string().uuid(),
  trackingId: z.string(),
  displayReference: z.string(),
  portionSubtotal: z.number(),
  portionTotal: z.number(),
  paymentMethod: z.string().optional(),
  stockLocationId: z.string().uuid().optional(),
  metadata: z.record(z.unknown()).optional()
});
var UpdateOrderPartitionDtoSchema = z.object({
  fulfillmentType: OrderPartitionFulfillmentTypeSchema.optional(),
  fulfillmentStatus: OrderPartitionFulfillmentStatusSchema.optional(),
  preparationStatus: OrderPartitionPreparationStatusSchema.optional(),
  paymentStatus: OrderPartitionPaymentStatusSchema.nullable().optional(),
  stockLocationId: z.string().uuid().nullable().optional(),
  onfleetTaskId: z.string().nullable().optional(),
  // Onfleet task reference
  shippingCarrier: z.string().nullable().optional(),
  // Mail carrier: usps, ups, fedex
  carrierTrackingNumber: z.string().nullable().optional(),
  shippedAt: z.date().nullable().optional(),
  easypostTrackerId: z.string().nullable().optional(),
  // EasyPost tracker reference (trk_...)
  spreadRate: z.number().nullable().optional(),
  spreadAmount: z.number().nullable().optional(),
  completedAt: z.date().nullable().optional(),
  // When fulfillment was completed
  completedBy: z.string().nullable().optional(),
  // Who completed: 'onfleet_webhook', 'manual:{userId}', 'reconciliation_job'
  hasSignature: z.boolean().optional(),
  photoCount: z.number().int().nonnegative().optional(),
  destinationOrganizationLocationId: z.string().uuid().nullable().optional(),
  metadata: z.record(z.unknown()).optional()
});
var OrderPartitionFiltersSchema = z.object({
  orderId: z.string().uuid().optional(),
  b2bwaveOrderId: z.string().optional(),
  brandId: z.string().uuid().optional(),
  brandIds: z.array(z.string().uuid()).optional(),
  fulfillmentStatus: OrderPartitionFulfillmentStatusSchema.optional(),
  fulfillmentStatuses: z.array(OrderPartitionFulfillmentStatusSchema).optional(),
  preparationStatus: OrderPartitionPreparationStatusSchema.optional(),
  paymentStatus: OrderPartitionPaymentStatusSchema.optional(),
  fulfillmentType: OrderPartitionFulfillmentTypeSchema.optional(),
  paymentMethod: z.string().optional(),
  fromDate: z.string().datetime().optional(),
  toDate: z.string().datetime().optional(),
  limit: z.number().int().positive().optional(),
  cursor: z.string().optional(),
  productSku: z.string().optional(),
  search: z.string().optional(),
  stockLocationId: z.string().uuid().optional(),
  // Special filter: flote_driver OR NULL (for delivery-admin)
  floteManaged: z.boolean().optional()
});
var PaginatedOrderPartitionsSchema = z.object({
  items: z.array(OrderPartitionDtoSchema),
  nextCursor: z.string().optional(),
  totalCount: z.number().int().optional()
});
var StalePartitionFiltersSchema = z.object({
  /** Fulfillment statuses to check (partitions in these statuses that should be completed) */
  statuses: z.array(OrderPartitionFulfillmentStatusSchema),
  /** Partitions older than this date are considered stale */
  olderThan: z.date().optional(),
  /** Only include partitions shipped before this date */
  shippedBefore: z.date().optional(),
  /** Only include partitions with an Onfleet task assigned */
  hasOnfleetTaskId: z.boolean().optional(),
  /** Only include partitions with an EasyPost tracker */
  hasEasypostTracker: z.boolean().optional()
});

// delivery/order-partition-note.schema.ts
import { z as z2 } from "zod";
var DbOrderPartitionNoteSchema = z2.object({
  id: z2.string().uuid(),
  order_partition_id: z2.string().uuid(),
  portal_user_id: z2.string().uuid(),
  content: z2.string(),
  created_at: z2.date(),
  updated_at: z2.date(),
  deleted_at: z2.date().nullable()
});
var OrderPartitionNoteSchema = z2.object({
  id: z2.string().uuid(),
  orderPartitionId: z2.string().uuid(),
  portalUserId: z2.string().uuid(),
  authorName: z2.string(),
  authorEmail: z2.string(),
  content: z2.string(),
  isEdited: z2.boolean(),
  createdAt: z2.date(),
  updatedAt: z2.date()
});
var CreateOrderPartitionNoteSchema = z2.object({
  orderPartitionId: z2.string().uuid(),
  portalUserId: z2.string().uuid(),
  content: z2.string().min(1).max(5e3)
});
var UpdateOrderPartitionNoteSchema = z2.object({
  content: z2.string().min(1).max(5e3)
});

// delivery/fulfillment-offer.schema.ts
import { z as z3 } from "zod";
var FulfillmentOfferStatusSchema = z3.enum([
  "pending",
  "accepted",
  "declined",
  "expired",
  "cancelled"
]);
var DbFulfillmentOfferSchema = z3.object({
  id: z3.string().uuid(),
  order_partition_id: z3.string().uuid(),
  status: FulfillmentOfferStatusSchema,
  expires_at: z3.union([z3.string(), z3.date()]),
  responded_at: z3.union([z3.string(), z3.date()]).nullable(),
  responded_by_portal_user_id: z3.string().uuid().nullable(),
  response_reason: z3.string().nullable(),
  created_at: z3.union([z3.string(), z3.date()])
});
var FulfillmentOfferSchema = z3.object({
  id: z3.string().uuid(),
  orderPartitionId: z3.string().uuid(),
  status: FulfillmentOfferStatusSchema,
  expiresAt: z3.date(),
  respondedAt: z3.date().nullable(),
  respondedByPortalUserId: z3.string().uuid().nullable(),
  responseReason: z3.string().nullable(),
  createdAt: z3.date()
});
var FulfillmentOfferDtoSchema = z3.object({
  id: z3.string().uuid(),
  orderPartitionId: z3.string().uuid(),
  status: FulfillmentOfferStatusSchema,
  expiresAt: z3.string().datetime(),
  respondedAt: z3.string().datetime().nullable(),
  respondedByPortalUserId: z3.string().uuid().nullable(),
  responseReason: z3.string().nullable(),
  createdAt: z3.string().datetime()
});
var CreateFulfillmentOfferDtoSchema = z3.object({
  orderPartitionId: z3.string().uuid(),
  expiresAt: z3.string().datetime()
});
var RespondToOfferDtoSchema = z3.object({
  accept: z3.boolean(),
  reason: z3.string().optional()
});
var FulfillmentOfferFiltersSchema = z3.object({
  orderPartitionId: z3.string().uuid().optional(),
  status: FulfillmentOfferStatusSchema.optional(),
  expiresBefore: z3.string().datetime().optional(),
  expiresAfter: z3.string().datetime().optional(),
  limit: z3.number().int().positive().optional(),
  cursor: z3.string().optional()
});
var PaginatedFulfillmentOffersSchema = z3.object({
  items: z3.array(FulfillmentOfferDtoSchema),
  nextCursor: z3.string().optional(),
  totalCount: z3.number().int().optional()
});
var FulfillmentOfferWithPartitionDtoSchema = FulfillmentOfferDtoSchema.extend({
  partition: z3.object({
    id: z3.string().uuid(),
    b2bwaveOrderId: z3.string(),
    brandId: z3.string().uuid(),
    trackingId: z3.string(),
    displayReference: z3.string().optional(),
    portionSubtotal: z3.number(),
    portionTotal: z3.number(),
    paymentMethod: z3.string().nullable()
  })
});
var OfferResponseDtoSchema = z3.object({
  offer: FulfillmentOfferDtoSchema,
  partition: OrderPartitionDtoSchema
});
var ExpireOffersResponseDtoSchema = z3.object({
  expiredCount: z3.number().int().nonnegative()
});
var PartitionItemDtoSchema = z3.object({
  id: z3.string().uuid(),
  orderPartitionId: z3.string().uuid(),
  productVariantId: z3.string().uuid(),
  productId: z3.string().uuid().optional(),
  quantity: z3.number().int().nonnegative(),
  reservedQuantity: z3.number().int().nonnegative(),
  fulfilledQuantity: z3.number().int().nonnegative(),
  unitPrice: z3.string(),
  lineTotal: z3.string(),
  productName: z3.string().optional(),
  productCode: z3.string().optional()
});
var FulfillmentOfferWithPartitionAndItemsDtoSchema = FulfillmentOfferDtoSchema.extend({
  partition: z3.object({
    id: z3.string().uuid(),
    b2bwaveOrderId: z3.string(),
    brandId: z3.string().uuid(),
    trackingId: z3.string(),
    displayReference: z3.string().optional(),
    portionSubtotal: z3.number(),
    portionTotal: z3.number(),
    paymentMethod: z3.string().nullable()
  }),
  items: z3.array(PartitionItemDtoSchema)
});

// delivery/order-partition-item.schema.ts
import { z as z4 } from "zod";
var DbOrderPartitionItemSchema = z4.object({
  id: z4.string().uuid(),
  order_partition_id: z4.string().uuid(),
  product_variant_id: z4.string().uuid(),
  inventory_item_id: z4.string().uuid().nullable(),
  b2bwave_line_item_id: z4.string().nullable(),
  quantity: z4.number().int().nonnegative(),
  reserved_quantity: z4.number().int().nonnegative(),
  fulfilled_quantity: z4.number().int().nonnegative(),
  unit_price: z4.string(),
  // DECIMAL stored as string
  line_total: z4.string(),
  fulfilled_at: z4.union([z4.string(), z4.date()]).nullable(),
  // When this item was fulfilled
  reserved_at: z4.union([z4.string(), z4.date()]).nullable(),
  // When inventory was reserved
  stock_deducted_at: z4.union([z4.string(), z4.date()]).nullable(),
  // When physical stock was deducted
  requires_metrc_tracking: z4.boolean(),
  metadata: z4.record(z4.unknown()),
  created_at: z4.union([z4.string(), z4.date()]),
  updated_at: z4.union([z4.string(), z4.date()])
});
var OrderPartitionItemSchema = z4.object({
  id: z4.string().uuid(),
  orderPartitionId: z4.string().uuid(),
  productVariantId: z4.string().uuid(),
  inventoryItemId: z4.string().uuid().nullable(),
  quantity: z4.number().int().nonnegative(),
  reservedQuantity: z4.number().int().nonnegative(),
  fulfilledQuantity: z4.number().int().nonnegative(),
  unitPrice: z4.string(),
  // DECIMAL stored as string
  lineTotal: z4.string(),
  fulfilledAt: z4.date().nullable(),
  // When this item was fulfilled
  reservedAt: z4.date().nullable(),
  // When inventory was reserved
  stockDeductedAt: z4.date().nullable(),
  // When physical stock was deducted (idempotency guard)
  requiresMetrcTracking: z4.boolean(),
  metadata: z4.record(z4.unknown()),
  createdAt: z4.date(),
  updatedAt: z4.date()
});
var CreateOrderPartitionItemDtoSchema = z4.object({
  orderPartitionId: z4.string().uuid(),
  productVariantId: z4.string().uuid(),
  b2bwaveLineItemId: z4.string().optional(),
  quantity: z4.number().int().nonnegative(),
  requiresMetrcTracking: z4.boolean().optional(),
  unitPrice: z4.string(),
  // DECIMAL stored as string
  lineTotal: z4.string(),
  metadata: z4.record(z4.unknown()).optional()
});
var OrderPartitionItemWithProductSchema = OrderPartitionItemSchema.extend({
  productId: z4.string().uuid().optional(),
  productName: z4.string().optional(),
  productCode: z4.string().optional(),
  productThumbnail: z4.string().url().optional()
});

// delivery/order-partition-item-batch-reservation.schema.ts
import { z as z5 } from "zod";
var DbOrderPartitionItemBatchReservationSchema = z5.object({
  id: z5.string().uuid(),
  order_partition_item_id: z5.string().uuid(),
  inventory_batch_id: z5.string().uuid(),
  inventory_item_id: z5.string().uuid(),
  stock_location_id: z5.string().uuid(),
  reserved_quantity: z5.number().int().positive(),
  released_quantity: z5.number().int().nonnegative(),
  consumed_quantity: z5.number().int().nonnegative(),
  metrc_package_tag_snapshot: z5.string().nullable(),
  batch_number_snapshot: z5.string().nullable(),
  lot_number_snapshot: z5.string().nullable(),
  received_date_snapshot: z5.union([z5.string(), z5.date()]),
  expiry_date_snapshot: z5.union([z5.string(), z5.date()]).nullable(),
  reserved_at: z5.union([z5.string(), z5.date()]),
  released_at: z5.union([z5.string(), z5.date()]).nullable(),
  consumed_at: z5.union([z5.string(), z5.date()]).nullable(),
  metadata: z5.record(z5.unknown()),
  created_at: z5.union([z5.string(), z5.date()]),
  updated_at: z5.union([z5.string(), z5.date()])
});
var OrderPartitionItemBatchReservationSchema = z5.object({
  id: z5.string().uuid(),
  orderPartitionItemId: z5.string().uuid(),
  inventoryBatchId: z5.string().uuid(),
  inventoryItemId: z5.string().uuid(),
  stockLocationId: z5.string().uuid(),
  reservedQuantity: z5.number().int().positive(),
  releasedQuantity: z5.number().int().nonnegative(),
  consumedQuantity: z5.number().int().nonnegative(),
  remainingQuantity: z5.number().int().nonnegative(),
  metrcPackageTagSnapshot: z5.string().nullable(),
  batchNumberSnapshot: z5.string().nullable(),
  lotNumberSnapshot: z5.string().nullable(),
  receivedDateSnapshot: z5.date(),
  expiryDateSnapshot: z5.date().nullable(),
  reservedAt: z5.date(),
  releasedAt: z5.date().nullable(),
  consumedAt: z5.date().nullable(),
  metadata: z5.record(z5.unknown()),
  createdAt: z5.date(),
  updatedAt: z5.date()
});
var CreateOrderPartitionItemBatchReservationDtoSchema = z5.object({
  orderPartitionItemId: z5.string().uuid(),
  inventoryBatchId: z5.string().uuid(),
  inventoryItemId: z5.string().uuid(),
  stockLocationId: z5.string().uuid(),
  reservedQuantity: z5.number().int().positive(),
  metrcPackageTagSnapshot: z5.null().optional(),
  batchNumberSnapshot: z5.string().nullable().optional(),
  lotNumberSnapshot: z5.string().nullable().optional(),
  receivedDateSnapshot: z5.date(),
  expiryDateSnapshot: z5.date().nullable().optional(),
  reservedAt: z5.date().optional(),
  metadata: z5.record(z5.unknown()).optional()
});

// delivery/order-partition-item-package-reservation.schema.ts
import { z as z6 } from "zod";
var DateOrStringSchema = z6.union([z6.string(), z6.date()]);
var DbOrderPartitionItemPackageReservationSchema = z6.object({
  id: z6.string().uuid(),
  order_partition_item_id: z6.string().uuid(),
  inventory_package_id: z6.string().uuid(),
  inventory_item_id: z6.string().uuid(),
  stock_location_id: z6.string().uuid(),
  facility_license_number: z6.string().nullable(),
  is_metrc_tracked: z6.boolean(),
  reserved_quantity: z6.number().nonnegative(),
  released_quantity: z6.number().nonnegative(),
  consumed_quantity: z6.number().nonnegative(),
  package_label_snapshot: z6.string().nullable(),
  production_batch_number_snapshot: z6.string().nullable(),
  lot_number_snapshot: z6.string().nullable(),
  received_date_snapshot: DateOrStringSchema.nullable(),
  expiry_date_snapshot: DateOrStringSchema.nullable(),
  reserved_at: DateOrStringSchema,
  released_at: DateOrStringSchema.nullable(),
  consumed_at: DateOrStringSchema.nullable(),
  consumed_reason: z6.string().nullable(),
  source_legacy_reservation_id: z6.string().uuid().nullable(),
  source_legacy_inventory_package_id: z6.string().uuid().nullable(),
  created_at: DateOrStringSchema,
  updated_at: DateOrStringSchema
});
var OrderPartitionItemPackageReservationSchema = z6.object({
  id: z6.string().uuid(),
  orderPartitionItemId: z6.string().uuid(),
  inventoryPackageId: z6.string().uuid(),
  inventoryItemId: z6.string().uuid(),
  stockLocationId: z6.string().uuid(),
  facilityLicenseNumber: z6.string().nullable(),
  isMetrcTracked: z6.boolean(),
  reservedQuantity: z6.number().nonnegative(),
  releasedQuantity: z6.number().nonnegative(),
  consumedQuantity: z6.number().nonnegative(),
  remainingQuantity: z6.number().nonnegative(),
  packageLabelSnapshot: z6.string().nullable(),
  productionBatchNumberSnapshot: z6.string().nullable(),
  lotNumberSnapshot: z6.string().nullable(),
  receivedDateSnapshot: z6.date().nullable(),
  expiryDateSnapshot: z6.date().nullable(),
  reservedAt: z6.date(),
  releasedAt: z6.date().nullable(),
  consumedAt: z6.date().nullable(),
  consumedReason: z6.string().nullable(),
  sourceLegacyReservationId: z6.string().uuid().nullable(),
  sourceLegacyInventoryPackageId: z6.string().uuid().nullable(),
  createdAt: z6.date(),
  updatedAt: z6.date()
});
var OrderPartitionItemPackageReservationDtoSchema = z6.object({
  id: z6.string().uuid(),
  orderPartitionItemId: z6.string().uuid(),
  inventoryPackageId: z6.string().uuid(),
  inventoryItemId: z6.string().uuid(),
  stockLocationId: z6.string().uuid(),
  facilityLicenseNumber: z6.string().nullable(),
  isMetrcTracked: z6.boolean(),
  reservedQuantity: z6.number().nonnegative(),
  releasedQuantity: z6.number().nonnegative(),
  consumedQuantity: z6.number().nonnegative(),
  remainingQuantity: z6.number().nonnegative(),
  packageLabelSnapshot: z6.string().nullable(),
  productionBatchNumberSnapshot: z6.string().nullable(),
  lotNumberSnapshot: z6.string().nullable(),
  receivedDateSnapshot: z6.string().nullable(),
  expiryDateSnapshot: z6.string().nullable(),
  reservedAt: z6.string(),
  releasedAt: z6.string().nullable(),
  consumedAt: z6.string().nullable(),
  consumedReason: z6.string().nullable(),
  sourceLegacyReservationId: z6.string().uuid().nullable(),
  sourceLegacyInventoryPackageId: z6.string().uuid().nullable(),
  createdAt: z6.string(),
  updatedAt: z6.string()
});
var CreateOrderPartitionItemPackageReservationDtoSchema = z6.object({
  orderPartitionItemId: z6.string().uuid(),
  inventoryPackageId: z6.string().uuid(),
  inventoryItemId: z6.string().uuid(),
  stockLocationId: z6.string().uuid(),
  facilityLicenseNumber: z6.string().nullable().optional(),
  isMetrcTracked: z6.boolean().default(false),
  reservedQuantity: z6.number().positive(),
  releasedQuantity: z6.number().nonnegative().optional(),
  consumedQuantity: z6.number().nonnegative().optional(),
  packageLabelSnapshot: z6.string().nullable().optional(),
  productionBatchNumberSnapshot: z6.string().nullable().optional(),
  lotNumberSnapshot: z6.string().nullable().optional(),
  receivedDateSnapshot: z6.date().nullable().optional(),
  expiryDateSnapshot: z6.date().nullable().optional(),
  reservedAt: z6.date().optional(),
  consumedReason: z6.string().nullable().optional(),
  sourceLegacyReservationId: z6.string().uuid().nullable().optional(),
  sourceLegacyInventoryPackageId: z6.string().uuid().nullable().optional()
});

// delivery/fulfillment-record.schema.ts
import { z as z7 } from "zod";
var FulfillmentRecordFulfillmentTypeSchema = z7.enum(["brand_driver", "flote_driver", "mail_carrier"]);
var DbFulfillmentRecordSchema = z7.object({
  id: z7.string().uuid(),
  order_partition_id: z7.string().uuid(),
  order_partition_item_id: z7.string().uuid(),
  inventory_item_id: z7.string().uuid().nullable(),
  brand_id: z7.string().uuid(),
  stock_location_id: z7.string().uuid().nullable(),
  b2bwave_order_id: z7.string().nullable(),
  quantity: z7.number().int().positive(),
  unit_price: z7.string(),
  // DECIMAL stored as string
  line_total: z7.string(),
  fulfillment_type: FulfillmentRecordFulfillmentTypeSchema.nullable(),
  delivery_spread_rate: z7.string().nullable(),
  delivery_spread_amount: z7.string().nullable(),
  fulfilled_at: z7.union([z7.string(), z7.date()]),
  fulfilled_by: z7.string(),
  metadata: z7.record(z7.unknown()),
  created_at: z7.union([z7.string(), z7.date()])
});
var FulfillmentRecordSchema = z7.object({
  id: z7.string().uuid(),
  orderPartitionId: z7.string().uuid(),
  orderPartitionItemId: z7.string().uuid(),
  inventoryItemId: z7.string().uuid().nullable(),
  brandId: z7.string().uuid(),
  stockLocationId: z7.string().uuid().nullable(),
  b2bwaveOrderId: z7.string().nullable(),
  quantity: z7.number().int().positive(),
  unitPrice: z7.number(),
  lineTotal: z7.number(),
  fulfillmentType: FulfillmentRecordFulfillmentTypeSchema.nullable(),
  deliverySpreadRate: z7.number().nullable(),
  deliverySpreadAmount: z7.number().nullable(),
  fulfilledAt: z7.date(),
  fulfilledBy: z7.string(),
  metadata: z7.record(z7.unknown()),
  createdAt: z7.date()
});
var FulfillmentRecordDtoSchema = z7.object({
  id: z7.string().uuid(),
  orderPartitionId: z7.string().uuid(),
  orderPartitionItemId: z7.string().uuid(),
  inventoryItemId: z7.string().uuid().nullable(),
  brandId: z7.string().uuid(),
  stockLocationId: z7.string().uuid().nullable(),
  b2bwaveOrderId: z7.string().nullable(),
  quantity: z7.number().int().positive(),
  unitPrice: z7.number(),
  lineTotal: z7.number(),
  fulfillmentType: FulfillmentRecordFulfillmentTypeSchema.nullable(),
  deliverySpreadRate: z7.number().nullable(),
  deliverySpreadAmount: z7.number().nullable(),
  fulfilledAt: z7.string().datetime(),
  fulfilledBy: z7.string(),
  metadata: z7.record(z7.unknown()),
  createdAt: z7.string().datetime()
});
var CreateFulfillmentRecordDtoSchema = z7.object({
  orderPartitionId: z7.string().uuid(),
  orderPartitionItemId: z7.string().uuid(),
  inventoryItemId: z7.string().uuid().nullable().optional(),
  brandId: z7.string().uuid(),
  stockLocationId: z7.string().uuid().nullable().optional(),
  b2bwaveOrderId: z7.string().nullable().optional(),
  quantity: z7.number().int().positive(),
  unitPrice: z7.number(),
  lineTotal: z7.number(),
  fulfillmentType: FulfillmentRecordFulfillmentTypeSchema.nullable().optional(),
  deliverySpreadRate: z7.number().nullable().optional(),
  deliverySpreadAmount: z7.number().nullable().optional(),
  fulfilledBy: z7.string(),
  metadata: z7.record(z7.unknown()).optional()
});
var FulfillmentRecordFiltersSchema = z7.object({
  orderPartitionId: z7.string().uuid().optional(),
  brandId: z7.string().uuid().optional(),
  stockLocationId: z7.string().uuid().optional(),
  fulfillmentType: FulfillmentRecordFulfillmentTypeSchema.optional(),
  fromDate: z7.string().datetime().optional(),
  toDate: z7.string().datetime().optional(),
  limit: z7.number().int().positive().optional(),
  cursor: z7.string().optional()
});
var BrandFulfillmentSummarySchema = z7.object({
  brandId: z7.string().uuid(),
  brandName: z7.string().optional(),
  date: z7.string(),
  // ISO date string (YYYY-MM-DD)
  ordersFulfilled: z7.number().int().nonnegative(),
  unitsSold: z7.number().int().nonnegative(),
  grossRevenue: z7.number(),
  deliveryFees: z7.number(),
  netRevenue: z7.number()
});
var StockLocationPerformanceSchema = z7.object({
  stockLocationId: z7.string().uuid(),
  locationName: z7.string().optional(),
  itemsFulfilled: z7.number().int().nonnegative(),
  unitsFulfilled: z7.number().int().nonnegative(),
  avgFulfillmentHours: z7.number().nullable()
});
var FulfillmentResultSchema = z7.object({
  success: z7.boolean(),
  alreadyCompleted: z7.boolean().optional(),
  itemsProcessed: z7.number().int().nonnegative().optional(),
  errors: z7.array(z7.object({
    itemId: z7.string().uuid(),
    error: z7.string(),
    errorCode: z7.string().optional(),
    errorStatus: z7.number().int().optional()
  })).optional()
});

// delivery/assignment-queue.schema.ts
import { z as z8 } from "zod";
var AssignmentQueueStatusSchema = z8.enum([
  "pending",
  // Awaiting assignment
  "processing",
  // Assignment in progress (optimistic lock)
  "assigned",
  // Driver assigned successfully
  "expired",
  // Offer/assignment window expired
  "cancelled"
  // Manually removed from queue
]);
var DbAssignmentQueueSchema = z8.object({
  id: z8.string().uuid(),
  partition_id: z8.string().uuid(),
  organization_id: z8.string().uuid().nullable(),
  supplier_id: z8.string().uuid().nullable(),
  stock_location_id: z8.string().uuid(),
  priority: z8.number().int(),
  enqueued_at: z8.union([z8.string(), z8.date()]),
  expires_at: z8.union([z8.string(), z8.date()]).nullable(),
  order_value: z8.string().nullable(),
  // DECIMAL stored as string
  status: AssignmentQueueStatusSchema,
  assigned_at: z8.union([z8.string(), z8.date()]).nullable(),
  assigned_driver_id: z8.string().uuid().nullable(),
  metadata: z8.record(z8.unknown()),
  created_at: z8.union([z8.string(), z8.date()]),
  updated_at: z8.union([z8.string(), z8.date()])
});
var AssignmentQueueEntrySchema = z8.object({
  id: z8.string().uuid(),
  partitionId: z8.string().uuid(),
  organizationId: z8.string().uuid().nullable(),
  supplierId: z8.string().uuid().nullable(),
  stockLocationId: z8.string().uuid(),
  priority: z8.number().int(),
  enqueuedAt: z8.date(),
  expiresAt: z8.date().nullable(),
  orderValue: z8.number().nullable(),
  status: AssignmentQueueStatusSchema,
  assignedAt: z8.date().nullable(),
  assignedDriverId: z8.string().uuid().nullable(),
  metadata: z8.record(z8.unknown()),
  createdAt: z8.date(),
  updatedAt: z8.date()
});
var AssignmentQueueEntryDtoSchema = z8.object({
  id: z8.string().uuid(),
  partitionId: z8.string().uuid(),
  organizationId: z8.string().uuid().nullable(),
  supplierId: z8.string().uuid().nullable(),
  stockLocationId: z8.string().uuid(),
  priority: z8.number().int(),
  enqueuedAt: z8.string().datetime(),
  expiresAt: z8.string().datetime().nullable(),
  orderValue: z8.number().nullable(),
  status: AssignmentQueueStatusSchema,
  assignedAt: z8.string().datetime().nullable(),
  assignedDriverId: z8.string().uuid().nullable(),
  metadata: z8.record(z8.unknown()),
  createdAt: z8.string().datetime(),
  updatedAt: z8.string().datetime(),
  // Enriched fields for display
  trackingId: z8.string().optional(),
  customerName: z8.string().optional(),
  portionTotal: z8.number().optional()
});
var CreateAssignmentQueueEntrySchema = z8.object({
  partitionId: z8.string().uuid(),
  organizationId: z8.string().uuid().nullable().optional(),
  supplierId: z8.string().uuid().nullable().optional(),
  stockLocationId: z8.string().uuid(),
  priority: z8.number().int().optional(),
  // Calculated if not provided
  expiresAt: z8.date().optional(),
  orderValue: z8.number().optional(),
  metadata: z8.record(z8.unknown()).optional()
});
var UpdateAssignmentQueueEntrySchema = z8.object({
  priority: z8.number().int().optional(),
  status: AssignmentQueueStatusSchema.optional(),
  assignedAt: z8.date().nullable().optional(),
  assignedDriverId: z8.string().uuid().nullable().optional(),
  metadata: z8.record(z8.unknown()).optional()
});
var AssignmentQueueFiltersSchema = z8.object({
  organizationId: z8.string().uuid().optional(),
  supplierId: z8.string().uuid().optional(),
  stockLocationId: z8.string().uuid().optional(),
  status: AssignmentQueueStatusSchema.optional(),
  statuses: z8.array(AssignmentQueueStatusSchema).optional(),
  expiringBefore: z8.date().optional(),
  limit: z8.number().int().positive().optional()
});
var AssignmentQueueStatsSchema = z8.object({
  pending: z8.number().int(),
  processing: z8.number().int(),
  assigned: z8.number().int(),
  expired: z8.number().int(),
  avgWaitTimeMs: z8.number().nullable(),
  // Average time from enqueue to assignment
  expiringWithin30Min: z8.number().int()
});

// delivery/shipment-leg.schema.ts
import { z as z9 } from "zod";
var GeoJsonLineStringSchema = z9.object({
  type: z9.literal("LineString"),
  coordinates: z9.array(z9.tuple([z9.number(), z9.number()])).min(2)
});
var DeliveryShipmentStatusSchema = z9.enum([
  "planned",
  "dispatched",
  "in_transit",
  "at_layover",
  "split",
  "completed",
  "cancelled"
]);
var DeliveryShipmentLegTypeSchema = z9.enum([
  "pickup",
  "linehaul",
  "layover_handoff",
  "final_mile",
  "staging_dropoff"
]);
var DeliveryShipmentLegStatusSchema = z9.enum([
  "planned",
  "queued",
  "assigned",
  "in_progress",
  "completed",
  "failed",
  "cancelled"
]);
var DeliveryShipmentLegAllocationStateSchema = z9.enum([
  "planned",
  "active",
  "transferred",
  "terminal_completed",
  "removed"
]);
var DeliveryShipmentLegQueueStatusSchema = z9.enum([
  "pending",
  "processing",
  "assigned",
  "expired",
  "cancelled"
]);
var DownstreamDraftPublishStatusSchema = z9.enum([
  "draft",
  "confirmed",
  "published",
  "publish_failed"
]);
var DeliveryShipmentSchema = z9.object({
  id: z9.string().uuid(),
  organizationId: z9.string().uuid(),
  sourceStockLocationId: z9.string().uuid(),
  status: DeliveryShipmentStatusSchema,
  metadata: z9.record(z9.unknown()),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var DeliveryShipmentLegSchema = z9.object({
  id: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  sequence: z9.number().int().positive(),
  legType: DeliveryShipmentLegTypeSchema,
  originLocationId: z9.string().uuid().nullable(),
  destinationLocationId: z9.string().uuid().nullable(),
  organizationId: z9.string().uuid(),
  status: DeliveryShipmentLegStatusSchema,
  assignedDriverId: z9.string().uuid().nullable(),
  onfleetTaskId: z9.string().nullable(),
  onfleetPickupTaskId: z9.string().nullable(),
  onfleetOrderId: z9.string().nullable(),
  onfleetRoutePlanId: z9.string().nullable(),
  fromNodeId: z9.string().uuid().nullable(),
  toNodeId: z9.string().uuid().nullable(),
  startedAt: z9.date().nullable(),
  completedAt: z9.date().nullable(),
  routeGeometry: GeoJsonLineStringSchema.nullable(),
  routeDistanceMeters: z9.number().int().nonnegative().nullable(),
  routeDurationSeconds: z9.number().int().nonnegative().nullable(),
  routeComputedAt: z9.date().nullable(),
  routeComputeError: z9.string().nullable(),
  metadata: z9.record(z9.unknown()),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var DeliveryShipmentLegAllocationSchema = z9.object({
  id: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  partitionId: z9.string().uuid(),
  allocationState: DeliveryShipmentLegAllocationStateSchema,
  isTerminalDestinationForPartition: z9.boolean(),
  finalRecipientLocationId: z9.string().uuid().nullable(),
  destinationComplianceLicenseId: z9.string().uuid().nullable(),
  metadata: z9.record(z9.unknown()),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var DeliveryShipmentLegMetrcRefSchema = z9.object({
  id: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  partitionId: z9.string().uuid().nullable(),
  metrcDeliveryId: z9.string().nullable(),
  metrcTransferId: z9.string().nullable(),
  metrcManifestNumber: z9.string().nullable(),
  isLayover: z9.boolean().nullable(),
  metrcTransporterFacilityLicenseNumber: z9.string().nullable(),
  metrcTransporterAcceptedAt: z9.date().nullable(),
  metrcEstimatedDepartureAt: z9.date().nullable(),
  metrcActualDepartureAt: z9.date().nullable(),
  metrcEstimatedArrivalAt: z9.date().nullable(),
  metrcActualArrivalAt: z9.date().nullable(),
  metadata: z9.record(z9.unknown()),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var DeliveryShipmentLegQueueEntrySchema = z9.object({
  id: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  organizationId: z9.string().uuid(),
  stockLocationId: z9.string().uuid(),
  priority: z9.number().int(),
  status: DeliveryShipmentLegQueueStatusSchema,
  assignedDriverId: z9.string().uuid().nullable(),
  expiresAt: z9.date().nullable(),
  enqueuedAt: z9.date(),
  assignedAt: z9.date().nullable(),
  metadata: z9.record(z9.unknown()),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var DeliveryShipmentLegQueueListItemSchema = DeliveryShipmentLegQueueEntrySchema.extend({
  shipmentId: z9.string().uuid(),
  legType: DeliveryShipmentLegTypeSchema,
  legStatus: DeliveryShipmentLegStatusSchema,
  originLocationId: z9.string().uuid().nullable(),
  originLocationName: z9.string().nullable(),
  destinationLocationId: z9.string().uuid().nullable(),
  destinationLocationName: z9.string().nullable(),
  partitionCount: z9.number().int().nonnegative(),
  partitionIds: z9.array(z9.string().uuid()),
  metrcManifestCount: z9.number().int().nonnegative()
});
var DeliveryDownstreamTaskDraftSchema = z9.object({
  id: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  partitionIds: z9.array(z9.string().uuid()),
  status: DownstreamDraftPublishStatusSchema,
  onfleetTaskId: z9.string().nullable(),
  failureReason: z9.string().nullable().optional(),
  metadata: z9.record(z9.unknown()).default({}),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var CreateDeliveryShipmentSchema = z9.object({
  organizationId: z9.string().uuid(),
  sourceStockLocationId: z9.string().uuid(),
  status: DeliveryShipmentStatusSchema.optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var UpdateDeliveryShipmentSchema = z9.object({
  sourceStockLocationId: z9.string().uuid().optional(),
  status: DeliveryShipmentStatusSchema.optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentFiltersSchema = z9.object({
  status: DeliveryShipmentStatusSchema.optional(),
  statuses: z9.array(DeliveryShipmentStatusSchema).optional(),
  sourceStockLocationId: z9.string().uuid().optional(),
  limit: z9.number().int().positive().optional()
});
var CreateDeliveryShipmentLegSchema = z9.object({
  shipmentId: z9.string().uuid(),
  sequence: z9.number().int().positive(),
  legType: DeliveryShipmentLegTypeSchema,
  originLocationId: z9.string().uuid().nullable().optional(),
  destinationLocationId: z9.string().uuid().nullable().optional(),
  organizationId: z9.string().uuid(),
  status: DeliveryShipmentLegStatusSchema.optional(),
  assignedDriverId: z9.string().uuid().nullable().optional(),
  onfleetTaskId: z9.string().nullable().optional(),
  onfleetPickupTaskId: z9.string().nullable().optional(),
  onfleetOrderId: z9.string().nullable().optional(),
  onfleetRoutePlanId: z9.string().nullable().optional(),
  fromNodeId: z9.string().uuid().nullable().optional(),
  toNodeId: z9.string().uuid().nullable().optional(),
  startedAt: z9.date().nullable().optional(),
  completedAt: z9.date().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var UpdateDeliveryShipmentLegSchema = z9.object({
  sequence: z9.number().int().positive().optional(),
  legType: DeliveryShipmentLegTypeSchema.optional(),
  originLocationId: z9.string().uuid().nullable().optional(),
  destinationLocationId: z9.string().uuid().nullable().optional(),
  status: DeliveryShipmentLegStatusSchema.optional(),
  assignedDriverId: z9.string().uuid().nullable().optional(),
  onfleetTaskId: z9.string().nullable().optional(),
  onfleetPickupTaskId: z9.string().nullable().optional(),
  onfleetOrderId: z9.string().nullable().optional(),
  onfleetRoutePlanId: z9.string().nullable().optional(),
  fromNodeId: z9.string().uuid().nullable().optional(),
  toNodeId: z9.string().uuid().nullable().optional(),
  startedAt: z9.date().nullable().optional(),
  completedAt: z9.date().nullable().optional(),
  routeGeometry: GeoJsonLineStringSchema.nullable().optional(),
  routeDistanceMeters: z9.number().int().nonnegative().nullable().optional(),
  routeDurationSeconds: z9.number().int().nonnegative().nullable().optional(),
  routeComputedAt: z9.date().nullable().optional(),
  routeComputeError: z9.string().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentLegFiltersSchema = z9.object({
  status: DeliveryShipmentLegStatusSchema.optional(),
  statuses: z9.array(DeliveryShipmentLegStatusSchema).optional(),
  legType: DeliveryShipmentLegTypeSchema.optional(),
  legTypes: z9.array(DeliveryShipmentLegTypeSchema).optional(),
  organizationId: z9.string().uuid().optional(),
  assignedDriverId: z9.string().uuid().optional(),
  limit: z9.number().int().positive().optional()
});
var CreateDeliveryShipmentLegAllocationSchema = z9.object({
  shipmentLegId: z9.string().uuid(),
  partitionId: z9.string().uuid(),
  allocationState: DeliveryShipmentLegAllocationStateSchema.optional(),
  isTerminalDestinationForPartition: z9.boolean().optional(),
  finalRecipientLocationId: z9.string().uuid().nullable().optional(),
  destinationComplianceLicenseId: z9.string().uuid().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var UpdateDeliveryShipmentLegAllocationSchema = z9.object({
  allocationState: DeliveryShipmentLegAllocationStateSchema.optional(),
  isTerminalDestinationForPartition: z9.boolean().optional(),
  finalRecipientLocationId: z9.string().uuid().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentLegAllocationFiltersSchema = z9.object({
  allocationState: DeliveryShipmentLegAllocationStateSchema.optional(),
  allocationStates: z9.array(DeliveryShipmentLegAllocationStateSchema).optional(),
  shipmentLegId: z9.string().uuid().optional(),
  partitionId: z9.string().uuid().optional(),
  limit: z9.number().int().positive().optional()
});
var CreateDeliveryShipmentLegMetrcRefSchema = z9.object({
  shipmentLegId: z9.string().uuid(),
  partitionId: z9.string().uuid().nullable().optional(),
  metrcDeliveryId: z9.string().nullable().optional(),
  metrcTransferId: z9.string().nullable().optional(),
  metrcManifestNumber: z9.string().nullable().optional(),
  isLayover: z9.boolean().nullable().optional(),
  metrcTransporterFacilityLicenseNumber: z9.string().nullable().optional(),
  metrcTransporterAcceptedAt: z9.date().nullable().optional(),
  metrcEstimatedDepartureAt: z9.date().nullable().optional(),
  metrcActualDepartureAt: z9.date().nullable().optional(),
  metrcEstimatedArrivalAt: z9.date().nullable().optional(),
  metrcActualArrivalAt: z9.date().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var UpdateDeliveryShipmentLegMetrcRefSchema = z9.object({
  metrcDeliveryId: z9.string().nullable().optional(),
  metrcTransferId: z9.string().nullable().optional(),
  metrcManifestNumber: z9.string().nullable().optional(),
  isLayover: z9.boolean().nullable().optional(),
  metrcTransporterFacilityLicenseNumber: z9.string().nullable().optional(),
  metrcTransporterAcceptedAt: z9.date().nullable().optional(),
  metrcEstimatedDepartureAt: z9.date().nullable().optional(),
  metrcActualDepartureAt: z9.date().nullable().optional(),
  metrcEstimatedArrivalAt: z9.date().nullable().optional(),
  metrcActualArrivalAt: z9.date().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentLegMetrcRefFiltersSchema = z9.object({
  partitionId: z9.string().uuid().optional(),
  metrcDeliveryId: z9.string().optional(),
  metrcTransferId: z9.string().optional(),
  metrcManifestNumber: z9.string().optional(),
  isLayover: z9.boolean().optional(),
  limit: z9.number().int().positive().optional()
});
var CreateDeliveryShipmentLegQueueEntrySchema = z9.object({
  shipmentLegId: z9.string().uuid(),
  organizationId: z9.string().uuid(),
  stockLocationId: z9.string().uuid(),
  priority: z9.number().int().optional(),
  status: DeliveryShipmentLegQueueStatusSchema.optional(),
  assignedDriverId: z9.string().uuid().nullable().optional(),
  expiresAt: z9.date().nullable().optional(),
  enqueuedAt: z9.date().optional(),
  assignedAt: z9.date().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var UpdateDeliveryShipmentLegQueueEntrySchema = z9.object({
  priority: z9.number().int().optional(),
  status: DeliveryShipmentLegQueueStatusSchema.optional(),
  assignedDriverId: z9.string().uuid().nullable().optional(),
  expiresAt: z9.date().nullable().optional(),
  assignedAt: z9.date().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentLegQueueFiltersSchema = z9.object({
  status: DeliveryShipmentLegQueueStatusSchema.optional(),
  statuses: z9.array(DeliveryShipmentLegQueueStatusSchema).optional(),
  shipmentStatuses: z9.array(DeliveryShipmentStatusSchema).optional(),
  stockLocationId: z9.string().uuid().optional(),
  limit: z9.number().int().positive().optional(),
  offset: z9.number().int().nonnegative().optional()
});
var DeliveryShipmentLegQueueStatsSchema = z9.object({
  pending: z9.number().int().nonnegative(),
  processing: z9.number().int().nonnegative(),
  assigned: z9.number().int().nonnegative(),
  expired: z9.number().int().nonnegative(),
  cancelled: z9.number().int().nonnegative(),
  avgWaitTimeMs: z9.number().nullable(),
  expiringWithin30Min: z9.number().int().nonnegative()
});
var DeliveryShipmentLegQueueSummarySchema = z9.object({
  shipmentId: z9.string().uuid(),
  shipmentStatus: DeliveryShipmentStatusSchema,
  originLocationId: z9.string().uuid().nullable(),
  originLocationName: z9.string().nullable(),
  destinationLocationId: z9.string().uuid().nullable(),
  destinationLocationName: z9.string().nullable(),
  legCount: z9.number().int().nonnegative(),
  partitionCount: z9.number().int().nonnegative(),
  pendingLegCount: z9.number().int().nonnegative(),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var DeliveryShipmentLegPlanAllocationInputSchema = z9.object({
  partitionId: z9.string().uuid(),
  allocationState: DeliveryShipmentLegAllocationStateSchema.optional(),
  isTerminalDestinationForPartition: z9.boolean().optional(),
  finalRecipientLocationId: z9.string().uuid().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentLegPlanInputSchema = z9.object({
  sequence: z9.number().int().positive(),
  legType: DeliveryShipmentLegTypeSchema,
  originLocationId: z9.string().uuid().nullable().optional(),
  destinationLocationId: z9.string().uuid().nullable().optional(),
  status: DeliveryShipmentLegStatusSchema.optional(),
  assignedDriverId: z9.string().uuid().nullable().optional(),
  onfleetTaskId: z9.string().nullable().optional(),
  onfleetPickupTaskId: z9.string().nullable().optional(),
  onfleetOrderId: z9.string().nullable().optional(),
  onfleetRoutePlanId: z9.string().nullable().optional(),
  startedAt: z9.date().nullable().optional(),
  completedAt: z9.date().nullable().optional(),
  partitionAllocations: z9.array(DeliveryShipmentLegPlanAllocationInputSchema).min(1),
  metadata: z9.record(z9.unknown()).optional()
});
var CreateDeliveryShipmentPlanInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  sourceStockLocationId: z9.string().uuid(),
  status: DeliveryShipmentStatusSchema.optional(),
  metadata: z9.record(z9.unknown()).optional(),
  legs: z9.array(DeliveryShipmentLegPlanInputSchema).min(1)
});
var CreateDeliveryShipmentPlanResultSchema = z9.object({
  shipment: DeliveryShipmentSchema,
  legs: z9.array(DeliveryShipmentLegSchema),
  allocations: z9.array(DeliveryShipmentLegAllocationSchema)
});
var AddHubSplitInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  /** The leg arriving at the hub stop */
  fromLegId: z9.string().uuid(),
  /** Display name for the hub node */
  hubName: z9.string().min(1).optional(),
  /** Stock location ID if the hub is a known warehouse */
  hubStockLocationId: z9.string().uuid().optional(),
  /** Partitions ending their journey at the hub (terminal deliveries) */
  terminalPartitionIds: z9.array(z9.string().uuid()).optional(),
  /** Driver to assign to created downstream legs */
  driverId: z9.string().uuid().optional(),
  /** If true and no driverId, add created legs to the assignment queue */
  queueForAssignment: z9.boolean().optional(),
  notes: z9.string().optional()
});
var AddHubSplitResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  hubNodeId: z9.string().uuid(),
  fromLegId: z9.string().uuid(),
  terminalCount: z9.number().int().nonnegative(),
  continuingCount: z9.number().int().nonnegative(),
  createdLegs: z9.array(DeliveryShipmentLegSchema),
  /** Driver ID if assigned during creation */
  assignedDriverId: z9.string().uuid().nullable().optional(),
  /** Leg IDs that were added to the assignment queue */
  queuedLegIds: z9.array(z9.string().uuid()).default([])
});
var RemoveHubSplitInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  hubNodeId: z9.string().uuid()
});
var RemoveHubSplitResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  removedHubNodeId: z9.string().uuid(),
  restoredLegId: z9.string().uuid(),
  deletedLegCount: z9.number().int().nonnegative(),
  restoredPartitionCount: z9.number().int().nonnegative()
});
var InsertWaypointInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  /** The edge to split by inserting a waypoint between its fromNode and toNode */
  fromLegId: z9.string().uuid(),
  /** Display name for the new waypoint/hub node */
  hubName: z9.string().min(1).optional(),
  /** Stock location ID if the waypoint is a known warehouse */
  hubStockLocationId: z9.string().uuid().optional(),
  /** Driver to assign to the new downstream edge */
  driverId: z9.string().uuid().optional(),
  /** If true and no driverId, add the new downstream edge to the assignment queue */
  queueForAssignment: z9.boolean().optional(),
  notes: z9.string().optional()
});
var InsertWaypointResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  hubNodeId: z9.string().uuid(),
  fromLegId: z9.string().uuid(),
  /** The new downstream edge (hub → original toNode) */
  createdLeg: DeliveryShipmentLegSchema,
  assignedDriverId: z9.string().uuid().nullable(),
  queuedLegId: z9.string().uuid().nullable()
});
var ReorderStopsInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  /** Destination node IDs in the desired order */
  orderedDestinationNodeIds: z9.array(z9.string().uuid()).min(1)
});
var ReorderStopsResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  reorderedLegCount: z9.number().int().nonnegative(),
  orderedDestinationNodeIds: z9.array(z9.string().uuid())
});
var OptimizeShipmentRouteInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid()
});
var OptimizeShipmentRouteSkipReasonSchema = z9.enum([
  "provider_unavailable",
  "insufficient_stops",
  "missing_coordinates",
  "no_optimization"
]);
var OptimizeShipmentRouteSegmentSchema = z9.object({
  driverId: z9.string().uuid().nullable(),
  rewiredLegCount: z9.number().int().nonnegative(),
  orderedNodeIds: z9.array(z9.string().uuid()),
  distanceMeters: z9.number().int().nonnegative().nullable(),
  durationSeconds: z9.number().int().nonnegative().nullable(),
  skipped: OptimizeShipmentRouteSkipReasonSchema.nullable()
});
var OptimizeShipmentRouteResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  /** Aggregate (sum) — legs whose (from_node_id, to_node_id) changed. Zero on skip. */
  rewiredLegCount: z9.number().int().nonnegative(),
  /** Aggregate (concat) — destination node IDs in segment-ascending visit order. Empty on skip. */
  orderedNodeIds: z9.array(z9.string().uuid()),
  /** Aggregate (sum) — distance across non-skipped segments. Null when all skipped. */
  distanceMeters: z9.number().int().nonnegative().nullable(),
  /** Aggregate (sum) — duration across non-skipped segments. Null when all skipped. */
  durationSeconds: z9.number().int().nonnegative().nullable(),
  /** Aggregate — null if any segment optimized; otherwise the first non-null skip reason. */
  skipped: OptimizeShipmentRouteSkipReasonSchema.nullable(),
  /** Per-driver-segment breakdown. Empty only on hard early returns. */
  segments: z9.array(OptimizeShipmentRouteSegmentSchema)
});
var OnfleetTaskKindSchema = z9.enum(["dropoff", "pickup"]);
var TaskReplaceReasonSchema = z9.enum([
  "initial_dispatch",
  "reroute_destination_change",
  "reroute_driver_change",
  "cancellation",
  "force_complete_recovery"
]);
var ShipmentLegTaskVersionSchema = z9.object({
  id: z9.string().uuid(),
  /** Null if the underlying leg has been deleted (ON DELETE SET NULL). */
  shipmentLegId: z9.string().uuid().nullable(),
  taskKind: OnfleetTaskKindSchema,
  onfleetTaskId: z9.string().min(1),
  assignedDriverId: z9.string().uuid().nullable(),
  version: z9.number().int().positive(),
  replaceReason: TaskReplaceReasonSchema,
  replacedAt: z9.date().nullable(),
  replacedByTaskVersionId: z9.string().uuid().nullable(),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var CreateShipmentLegTaskVersionSchema = z9.object({
  shipmentLegId: z9.string().uuid(),
  taskKind: OnfleetTaskKindSchema,
  onfleetTaskId: z9.string().min(1),
  assignedDriverId: z9.string().uuid().nullable(),
  version: z9.number().int().positive(),
  replaceReason: TaskReplaceReasonSchema
});
var BatchInsertHubInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  /** Which origin→dest legs to route through this hub */
  legIds: z9.array(z9.string().uuid()).min(1),
  /** Display name for the hub node */
  hubName: z9.string().min(1),
  /** Stock location ID if the hub is a known warehouse */
  hubStockLocationId: z9.string().uuid().optional(),
  /** Explicit driver for the shared origin→hub leg (resolves conflicts) */
  inboundDriverId: z9.string().uuid().optional(),
  /** Layover schedule for overnight hub stays */
  layoverSchedule: z9.object({
    checkInAt: z9.string(),
    checkOutAt: z9.string()
  }).optional()
});
var BatchInsertHubResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  hubNodeId: z9.string().uuid(),
  /** The shared origin→hub leg */
  inboundLegId: z9.string().uuid(),
  /** New hub→dest legs (one per selected destination) */
  outboundLegIds: z9.array(z9.string().uuid()),
  /** Original origin→dest legs that were replaced */
  removedLegIds: z9.array(z9.string().uuid())
});
var UpdateHubMetadataInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  hubNodeId: z9.string().uuid(),
  layoverSchedule: z9.object({
    checkInAt: z9.string(),
    checkOutAt: z9.string()
  }).nullable()
});
var UpdateHubMetadataResultSchema = z9.object({
  hubNodeId: z9.string().uuid(),
  metadata: z9.record(z9.unknown())
});
var CreateShipmentFromPartitionsInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  partitionIds: z9.array(z9.string().uuid()).min(1),
  queueForAssignment: z9.boolean().optional(),
  notes: z9.string().optional(),
  /**
   * When true or omitted (default), runs Mapbox route optimization after the
   * star topology is built so colinear destinations chain together. Set false
   * to preserve the raw per-destination star (e.g. when a dispatcher wants
   * parallel departures for SLA reasons).
   */
  optimizeRoute: z9.boolean().optional()
});
var CreateShipmentFromPartitionsResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  legCount: z9.number().int().positive(),
  destinationCount: z9.number().int().nonnegative(),
  untrackedPartitionCount: z9.number().int().nonnegative(),
  queuedLegIds: z9.array(z9.string().uuid())
});
var AssignDestinationLicenseInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  partitionIds: z9.array(z9.string().uuid()).min(1),
  complianceLicenseId: z9.string().uuid()
});
var AssignDestinationLicenseResultSchema = z9.object({
  destinationNodeId: z9.string().uuid(),
  destinationNodeName: z9.string(),
  complianceLicenseId: z9.string().uuid(),
  assignedPartitionCount: z9.number().int().nonnegative(),
  createdAllocations: z9.number().int().nonnegative()
});
var AttachPartitionsToShipmentLegInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  allocations: z9.array(DeliveryShipmentLegPlanAllocationInputSchema).min(1)
});
var DetachPartitionFromShipmentLegInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  partitionId: z9.string().uuid(),
  markRemoved: z9.boolean().default(true)
});
var GetDeliveryShipmentDetailInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid()
});
var DeliveryShipmentTimelineItemSchema = z9.object({
  shipmentId: z9.string().uuid(),
  legId: z9.string().uuid(),
  legSequence: z9.number().int().positive(),
  legType: DeliveryShipmentLegTypeSchema,
  legStatus: DeliveryShipmentLegStatusSchema,
  partitionIds: z9.array(z9.string().uuid()),
  terminalPartitionIds: z9.array(z9.string().uuid()),
  transferredPartitionIds: z9.array(z9.string().uuid()),
  startedAt: z9.date().nullable(),
  completedAt: z9.date().nullable(),
  metadata: z9.record(z9.unknown())
});
var GetDeliveryShipmentDetailResultSchema = z9.object({
  shipment: DeliveryShipmentSchema,
  legs: z9.array(DeliveryShipmentLegSchema),
  allocations: z9.array(DeliveryShipmentLegAllocationSchema),
  timeline: z9.array(DeliveryShipmentTimelineItemSchema),
  /** @deprecated Draft concept removed - always returns empty array */
  downstreamDrafts: z9.array(DeliveryDownstreamTaskDraftSchema).default([])
});
var UpdateShipmentLegInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  /** New destination location (only for linehaul legs) */
  destinationLocationId: z9.string().uuid().nullable().optional(),
  /** Driver to assign (or null to clear) */
  assignedDriverId: z9.string().uuid().nullable().optional(),
  /** Notes to update */
  notes: z9.string().optional()
});
var UpdateShipmentLegResultSchema = z9.object({
  leg: DeliveryShipmentLegSchema
});
var DeleteShipmentInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  force: z9.boolean().default(false)
});
var DeleteShipmentResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  deletedLegCount: z9.number().int().nonnegative(),
  deletedAllocationCount: z9.number().int().nonnegative(),
  deletedQueueEntryCount: z9.number().int().nonnegative(),
  cancelledOnfleetTaskIds: z9.array(z9.string()),
  warnings: z9.array(z9.string())
});
var DeleteShipmentLegInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  force: z9.boolean().default(false)
});
var DeleteShipmentLegResultSchema = z9.object({
  shipmentLegId: z9.string().uuid(),
  deletedAllocationCount: z9.number().int().nonnegative(),
  deletedQueueEntry: z9.boolean(),
  cancelledOnfleetTaskId: z9.string().nullable(),
  warnings: z9.array(z9.string())
});
var CancelQueueEntryInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid()
});
var CancelQueueEntryResultSchema = z9.object({
  shipmentLegId: z9.string().uuid(),
  queueEntryId: z9.string().uuid().nullable(),
  legStatusReverted: z9.boolean()
});
var ShipmentLegExecutionStrategySchema = z9.enum(["tasks", "orders"]);
var EnsureShipmentLegExecutionInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  strategy: ShipmentLegExecutionStrategySchema.default("tasks"),
  driverId: z9.string().uuid().optional()
});
var EnsureShipmentLegExecutionResultSchema = z9.object({
  shipmentLeg: DeliveryShipmentLegSchema,
  strategy: ShipmentLegExecutionStrategySchema,
  partitionIds: z9.array(z9.string().uuid()),
  onfleetTaskId: z9.string().nullable(),
  onfleetOrderId: z9.string().nullable(),
  groupedPartitionCount: z9.number().int().positive()
});
var DispatchShipmentInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentId: z9.string().uuid(),
  plannedDepartureTime: z9.number().int().positive().optional()
});
var DispatchShipmentPlanSchema = z9.object({
  driverId: z9.string().uuid(),
  driverName: z9.string(),
  routePlanId: z9.string(),
  taskCount: z9.number().int().nonnegative()
});
var DispatchShipmentResultSchema = z9.object({
  shipmentId: z9.string().uuid(),
  shipmentStatus: z9.literal("dispatched"),
  plans: z9.array(DispatchShipmentPlanSchema),
  warnings: z9.array(z9.string()),
  partitionsDispatched: z9.number().int().nonnegative(),
  partitionsSkipped: z9.number().int().nonnegative()
});
var DeliveryShipmentLegManifestStatusSchema = z9.enum([
  "draft",
  "ready",
  "submitted",
  "departed",
  "arrived",
  "voided"
]);
var DeliveryShipmentLegManifestFlowSchema = z9.enum([
  "templates_outgoing",
  "external_incoming"
]);
var DeliveryShipmentLegManifestReadinessIssueSchema = z9.object({
  code: z9.string().min(1),
  message: z9.string().min(1),
  field: z9.string().optional(),
  details: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentLegManifestReadinessSchema = z9.object({
  isReady: z9.boolean(),
  blockingErrors: z9.array(DeliveryShipmentLegManifestReadinessIssueSchema),
  warnings: z9.array(DeliveryShipmentLegManifestReadinessIssueSchema),
  info: z9.array(DeliveryShipmentLegManifestReadinessIssueSchema).default([]),
  checkedAt: z9.date(),
  metadata: z9.record(z9.unknown()).default({})
});
var DeliveryShipmentLegManifestShipperSchema = z9.object({
  facilityLicenseNumber: z9.string().min(1).nullable().optional(),
  facilityName: z9.string().nullable().optional()
});
var DeliveryShipmentLegManifestDestinationSchema = z9.object({
  recipientFacilityLicenseNumber: z9.string().min(1).nullable().optional(),
  recipientFacilityName: z9.string().nullable().optional(),
  plannedRoute: z9.string().nullable().optional(),
  estimatedDepartureAt: z9.date().nullable().optional(),
  estimatedArrivalAt: z9.date().nullable().optional(),
  actualDepartureAt: z9.date().nullable().optional(),
  actualArrivalAt: z9.date().nullable().optional()
});
var DeliveryShipmentLegManifestTransporterSchema = z9.object({
  transporterFacilityLicenseNumber: z9.string().nullable().optional(),
  transporterFacilityName: z9.string().nullable().optional(),
  driverName: z9.string().nullable().optional(),
  driverOccupationalLicenseNumber: z9.string().nullable().optional(),
  driverVehicleLicenseNumber: z9.string().nullable().optional(),
  vehicleMake: z9.string().nullable().optional(),
  vehicleModel: z9.string().nullable().optional(),
  vehicleLicensePlateNumber: z9.string().nullable().optional()
});
var DeliveryShipmentLegManifestPackageSchema = z9.object({
  packageLabel: z9.string().min(1),
  quantity: z9.number().nonnegative(),
  unitOfMeasureName: z9.string().nullable().optional(),
  grossWeight: z9.number().nullable().optional(),
  grossUnitOfWeightName: z9.string().nullable().optional(),
  productName: z9.string().nullable().optional(),
  metadata: z9.record(z9.unknown()).default({})
});
var DeliveryShipmentLegManifestPayloadSnapshotSchema = z9.object({
  flow: DeliveryShipmentLegManifestFlowSchema.default("templates_outgoing"),
  transferTypeName: z9.string().nullable().optional(),
  name: z9.string().nullable().optional(),
  plannedRoute: z9.string().nullable().optional(),
  shipper: DeliveryShipmentLegManifestShipperSchema,
  destinations: z9.array(DeliveryShipmentLegManifestDestinationSchema).default([]),
  transporters: z9.array(DeliveryShipmentLegManifestTransporterSchema).default([]),
  packages: z9.array(DeliveryShipmentLegManifestPackageSchema).default([]),
  metadata: z9.record(z9.unknown()).default({})
});
var DeliveryShipmentLegManifestSchema = z9.object({
  id: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  organizationId: z9.string().uuid(),
  status: DeliveryShipmentLegManifestStatusSchema,
  version: z9.number().int().positive(),
  metrcFlow: DeliveryShipmentLegManifestFlowSchema,
  metrcTransferId: z9.number().int().positive().nullable(),
  metrcManifestNumber: z9.string().nullable(),
  payloadSnapshot: DeliveryShipmentLegManifestPayloadSnapshotSchema,
  readinessSnapshot: DeliveryShipmentLegManifestReadinessSchema.nullable(),
  lastPreparedAt: z9.date().nullable(),
  submittedAt: z9.date().nullable(),
  departedAt: z9.date().nullable(),
  arrivedAt: z9.date().nullable(),
  voidedAt: z9.date().nullable(),
  metadata: z9.record(z9.unknown()),
  createdAt: z9.date(),
  updatedAt: z9.date()
});
var CreateDeliveryShipmentLegManifestSchema = z9.object({
  shipmentLegId: z9.string().uuid(),
  organizationId: z9.string().uuid(),
  status: DeliveryShipmentLegManifestStatusSchema.default("draft"),
  version: z9.number().int().positive().default(1),
  metrcFlow: DeliveryShipmentLegManifestFlowSchema.default("templates_outgoing"),
  metrcTransferId: z9.number().int().positive().nullable().optional(),
  metrcManifestNumber: z9.string().nullable().optional(),
  payloadSnapshot: DeliveryShipmentLegManifestPayloadSnapshotSchema,
  readinessSnapshot: DeliveryShipmentLegManifestReadinessSchema.nullable().optional(),
  lastPreparedAt: z9.date().nullable().optional(),
  submittedAt: z9.date().nullable().optional(),
  departedAt: z9.date().nullable().optional(),
  arrivedAt: z9.date().nullable().optional(),
  voidedAt: z9.date().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var UpdateDeliveryShipmentLegManifestSchema = z9.object({
  status: DeliveryShipmentLegManifestStatusSchema.optional(),
  version: z9.number().int().positive().optional(),
  metrcFlow: DeliveryShipmentLegManifestFlowSchema.optional(),
  metrcTransferId: z9.number().int().positive().nullable().optional(),
  metrcManifestNumber: z9.string().nullable().optional(),
  payloadSnapshot: DeliveryShipmentLegManifestPayloadSnapshotSchema.optional(),
  readinessSnapshot: DeliveryShipmentLegManifestReadinessSchema.nullable().optional(),
  lastPreparedAt: z9.date().nullable().optional(),
  submittedAt: z9.date().nullable().optional(),
  departedAt: z9.date().nullable().optional(),
  arrivedAt: z9.date().nullable().optional(),
  voidedAt: z9.date().nullable().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentLegManifestFiltersSchema = z9.object({
  shipmentLegId: z9.string().uuid().optional(),
  status: DeliveryShipmentLegManifestStatusSchema.optional(),
  statuses: z9.array(DeliveryShipmentLegManifestStatusSchema).optional(),
  metrcTransferId: z9.number().int().positive().optional(),
  limit: z9.number().int().positive().optional()
});
var GetShipmentLegManifestInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid()
});
var PrepareShipmentLegManifestInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  shipmentLegId: z9.string().uuid(),
  manifestName: z9.string().min(1).optional(),
  plannedRoute: z9.string().min(1).optional(),
  transferTypeName: z9.string().min(1).optional(),
  force: z9.boolean().default(false),
  metadata: z9.record(z9.unknown()).optional()
});
var PrepareShipmentLegManifestResultSchema = z9.object({
  manifest: DeliveryShipmentLegManifestSchema,
  readiness: DeliveryShipmentLegManifestReadinessSchema
});
var SubmitShipmentLegManifestInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  memberId: z9.string().uuid().optional(),
  shipmentLegId: z9.string().uuid(),
  expectedVersion: z9.number().int().positive().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var UpdateShipmentLegManifestInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  memberId: z9.string().uuid().optional(),
  shipmentLegId: z9.string().uuid(),
  expectedVersion: z9.number().int().positive().optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var VoidShipmentLegManifestInputSchema = z9.object({
  organizationId: z9.string().uuid(),
  memberId: z9.string().uuid().optional(),
  shipmentLegId: z9.string().uuid(),
  expectedVersion: z9.number().int().positive().optional(),
  reason: z9.string().min(1).optional(),
  metadata: z9.record(z9.unknown()).optional()
});
var DeliveryShipmentDtoSchema = DeliveryShipmentSchema.extend({
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryShipmentLegDtoSchema = DeliveryShipmentLegSchema.extend({
  startedAt: z9.string().datetime().nullable(),
  completedAt: z9.string().datetime().nullable(),
  routeComputedAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryShipmentLegAllocationDtoSchema = DeliveryShipmentLegAllocationSchema.extend({
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryShipmentLegManifestReadinessDtoSchema = DeliveryShipmentLegManifestReadinessSchema.extend({
  checkedAt: z9.string().datetime()
});
var DeliveryShipmentLegManifestDestinationDtoSchema = DeliveryShipmentLegManifestDestinationSchema.extend({
  estimatedDepartureAt: z9.string().datetime().nullable().optional(),
  estimatedArrivalAt: z9.string().datetime().nullable().optional(),
  actualDepartureAt: z9.string().datetime().nullable().optional(),
  actualArrivalAt: z9.string().datetime().nullable().optional()
});
var DeliveryShipmentLegManifestPayloadSnapshotDtoSchema = DeliveryShipmentLegManifestPayloadSnapshotSchema.extend({
  destinations: z9.array(DeliveryShipmentLegManifestDestinationDtoSchema).default([])
});
var DeliveryShipmentLegManifestDtoSchema = DeliveryShipmentLegManifestSchema.extend({
  payloadSnapshot: DeliveryShipmentLegManifestPayloadSnapshotDtoSchema,
  readinessSnapshot: DeliveryShipmentLegManifestReadinessDtoSchema.nullable(),
  lastPreparedAt: z9.string().datetime().nullable(),
  submittedAt: z9.string().datetime().nullable(),
  departedAt: z9.string().datetime().nullable(),
  arrivedAt: z9.string().datetime().nullable(),
  voidedAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryShipmentLegMetrcRefDtoSchema = DeliveryShipmentLegMetrcRefSchema.extend({
  metrcTransporterAcceptedAt: z9.string().datetime().nullable(),
  metrcEstimatedDepartureAt: z9.string().datetime().nullable(),
  metrcActualDepartureAt: z9.string().datetime().nullable(),
  metrcEstimatedArrivalAt: z9.string().datetime().nullable(),
  metrcActualArrivalAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryShipmentLegQueueEntryDtoSchema = DeliveryShipmentLegQueueEntrySchema.extend({
  expiresAt: z9.string().datetime().nullable(),
  enqueuedAt: z9.string().datetime(),
  assignedAt: z9.string().datetime().nullable(),
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryShipmentLegQueueListItemDtoSchema = DeliveryShipmentLegQueueEntryDtoSchema.extend({
  shipmentId: z9.string().uuid(),
  legType: DeliveryShipmentLegTypeSchema,
  legStatus: DeliveryShipmentLegStatusSchema,
  originLocationId: z9.string().uuid().nullable(),
  originLocationName: z9.string().nullable(),
  destinationLocationId: z9.string().uuid().nullable(),
  destinationLocationName: z9.string().nullable(),
  partitionCount: z9.number().int().nonnegative(),
  partitionIds: z9.array(z9.string().uuid()),
  metrcManifestCount: z9.number().int().nonnegative()
});
var DeliveryShipmentLegQueueSummaryDtoSchema = DeliveryShipmentLegQueueSummarySchema.extend({
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryDownstreamTaskDraftDtoSchema = DeliveryDownstreamTaskDraftSchema.extend({
  createdAt: z9.string().datetime(),
  updatedAt: z9.string().datetime()
});
var DeliveryShipmentTimelineItemDtoSchema = DeliveryShipmentTimelineItemSchema.extend({
  startedAt: z9.string().datetime().nullable(),
  completedAt: z9.string().datetime().nullable()
});

// delivery/shipment-auxiliary-task.schema.ts
import { z as z10 } from "zod";
var ShipmentAuxiliaryTaskCategorySchema = z10.enum(["hub_waypoint", "operational"]);
var ShipmentAuxiliaryTaskTypeSchema = z10.enum([
  "pickup",
  "dropoff",
  "handoff",
  "inspection",
  "other"
]);
var ShipmentAuxiliaryTaskStatusSchema = z10.enum(["pending", "completed", "cancelled"]);
var AuxiliaryTaskAddressSchema = z10.object({
  address1: z10.string(),
  address2: z10.string().nullable().optional(),
  city: z10.string(),
  province: z10.string(),
  postalCode: z10.string()
});
var ShipmentAuxiliaryTaskSchema = z10.object({
  id: z10.string().uuid(),
  shipmentId: z10.string().uuid(),
  shipmentLegId: z10.string().uuid().nullable(),
  taskCategory: ShipmentAuxiliaryTaskCategorySchema,
  taskType: ShipmentAuxiliaryTaskTypeSchema,
  onfleetTaskId: z10.string().nullable(),
  planNodeId: z10.string().uuid().nullable(),
  description: z10.string().nullable(),
  address: AuxiliaryTaskAddressSchema.nullable(),
  recipientName: z10.string().nullable(),
  recipientPhone: z10.string().nullable(),
  sortOrder: z10.number().int(),
  status: ShipmentAuxiliaryTaskStatusSchema,
  metadata: z10.record(z10.unknown()),
  createdAt: z10.date(),
  updatedAt: z10.date()
});
var CreateShipmentAuxiliaryTaskSchema = z10.object({
  shipmentId: z10.string().uuid(),
  shipmentLegId: z10.string().uuid().nullable().optional(),
  taskCategory: ShipmentAuxiliaryTaskCategorySchema,
  taskType: ShipmentAuxiliaryTaskTypeSchema,
  onfleetTaskId: z10.string().nullable().optional(),
  planNodeId: z10.string().uuid().nullable().optional(),
  description: z10.string().nullable().optional(),
  address: AuxiliaryTaskAddressSchema.nullable().optional(),
  recipientName: z10.string().nullable().optional(),
  recipientPhone: z10.string().nullable().optional(),
  sortOrder: z10.number().int().optional(),
  status: ShipmentAuxiliaryTaskStatusSchema.optional(),
  metadata: z10.record(z10.unknown()).optional()
});
var UpdateShipmentAuxiliaryTaskSchema = z10.object({
  shipmentLegId: z10.string().uuid().nullable().optional(),
  onfleetTaskId: z10.string().nullable().optional(),
  description: z10.string().nullable().optional(),
  address: AuxiliaryTaskAddressSchema.nullable().optional(),
  recipientName: z10.string().nullable().optional(),
  recipientPhone: z10.string().nullable().optional(),
  sortOrder: z10.number().int().optional(),
  status: ShipmentAuxiliaryTaskStatusSchema.optional(),
  metadata: z10.record(z10.unknown()).optional()
});

// delivery/shipment-leg-metrc-ops.schema.ts
import { z as z11 } from "zod";
var ShipmentLegMetrcRefSourceSchema = z11.enum([
  "partition_metadata",
  "shipment_leg_metadata",
  "backfill",
  "manual"
]);
var ShipmentLegMetrcLinkInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  shipmentLegId: z11.string().uuid(),
  partitionId: z11.string().uuid().nullable().optional(),
  partitionMetadata: z11.record(z11.unknown()).optional(),
  legMetadata: z11.record(z11.unknown()).optional(),
  source: ShipmentLegMetrcRefSourceSchema.default("manual"),
  ensureRow: z11.boolean().default(true),
  metadata: z11.record(z11.unknown()).optional()
});
var ShipmentLegMetrcBackfillInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  fromDate: z11.date().optional(),
  toDate: z11.date().optional(),
  partitionIds: z11.array(z11.string().uuid()).default([]),
  limit: z11.number().int().positive().default(1e3),
  apply: z11.boolean().default(false)
});
var ShipmentLegMetrcBackfillPartitionQuerySchema = z11.object({
  organizationId: z11.string().uuid(),
  fromDate: z11.date().optional(),
  toDate: z11.date().optional(),
  limit: z11.number().int().positive().default(1e3)
});
var ShipmentLegMetrcBackfillRecordCategorySchema = z11.enum([
  "mapped",
  "ambiguous",
  "unmapped",
  "failed"
]);
var ShipmentLegMetrcBackfillRecordSchema = z11.object({
  category: ShipmentLegMetrcBackfillRecordCategorySchema,
  partitionId: z11.string().uuid(),
  shipmentLegId: z11.string().uuid().nullable(),
  reason: z11.string(),
  metrcManifestNumber: z11.string().nullable().optional(),
  details: z11.record(z11.unknown()).optional()
});
var ShipmentLegMetrcBackfillMetricsSchema = z11.object({
  scannedPartitions: z11.number().int().nonnegative(),
  mapped: z11.number().int().nonnegative(),
  ambiguous: z11.number().int().nonnegative(),
  unmapped: z11.number().int().nonnegative(),
  failed: z11.number().int().nonnegative()
});
var ShipmentLegMetrcBackfillReportSchema = z11.object({
  dryRun: z11.boolean(),
  scope: ShipmentLegMetrcBackfillInputSchema,
  metrics: ShipmentLegMetrcBackfillMetricsSchema,
  records: z11.array(ShipmentLegMetrcBackfillRecordSchema)
});
var ShipmentLegMetrcReconciliationInputSchema = z11.object({
  organizationId: z11.string().uuid(),
  fromDate: z11.date().optional(),
  toDate: z11.date().optional(),
  limit: z11.number().int().positive().default(1e3)
});
var ShipmentLegMetrcReconciliationIssueTypeSchema = z11.enum([
  "missing_leg_level_ref",
  "missing_partition_ref",
  "missing_manifest_number",
  "missing_delivery_id",
  "missing_transfer_id",
  "manifest_reused_within_leg"
]);
var ShipmentLegMetrcReconciliationIssueSchema = z11.object({
  issueType: ShipmentLegMetrcReconciliationIssueTypeSchema,
  organizationId: z11.string().uuid(),
  shipmentId: z11.string().uuid(),
  shipmentLegId: z11.string().uuid(),
  partitionId: z11.string().uuid().nullable().optional(),
  metrcManifestNumber: z11.string().nullable().optional(),
  message: z11.string(),
  expected: z11.string().nullable().optional(),
  actual: z11.string().nullable().optional()
});
var ShipmentLegMetrcReconciliationMetricsSchema = z11.object({
  legsChecked: z11.number().int().nonnegative(),
  allocationsChecked: z11.number().int().nonnegative(),
  refsChecked: z11.number().int().nonnegative(),
  missingLegLevelRefs: z11.number().int().nonnegative(),
  missingPartitionRefs: z11.number().int().nonnegative(),
  missingManifestNumbers: z11.number().int().nonnegative(),
  missingDeliveryIds: z11.number().int().nonnegative(),
  missingTransferIds: z11.number().int().nonnegative(),
  manifestReuseConflicts: z11.number().int().nonnegative(),
  issuesFound: z11.number().int().nonnegative()
});
var ShipmentLegMetrcReconciliationReportSchema = z11.object({
  scope: ShipmentLegMetrcReconciliationInputSchema,
  metrics: ShipmentLegMetrcReconciliationMetricsSchema,
  issues: z11.array(ShipmentLegMetrcReconciliationIssueSchema)
});
var ShipmentLegMetrcRefFieldSubsetSchema = UpdateDeliveryShipmentLegMetrcRefSchema.pick({
  metrcDeliveryId: true,
  metrcTransferId: true,
  metrcManifestNumber: true,
  isLayover: true,
  metrcTransporterFacilityLicenseNumber: true,
  metrcTransporterAcceptedAt: true,
  metrcEstimatedDepartureAt: true,
  metrcActualDepartureAt: true,
  metrcEstimatedArrivalAt: true,
  metrcActualArrivalAt: true
});

// delivery/proof-asset.schema.ts
import { z as z12 } from "zod";
var DeliveryProofAssetTypeSchema = z12.enum(["photo", "signature"]);
var DeliveryProofAssetStatusSchema = z12.enum(["pending", "synced", "failed"]);
var DbDeliveryPartitionProofAssetSchema = z12.object({
  id: z12.string().uuid(),
  partition_id: z12.string().uuid(),
  task_id: z12.string().uuid(),
  onfleet_task_short_id: z12.string().nullable(),
  asset_type: DeliveryProofAssetTypeSchema,
  onfleet_upload_id: z12.string(),
  source_url: z12.string().nullable(),
  r2_bucket: z12.string().nullable(),
  r2_key: z12.string().nullable(),
  public_url: z12.string().nullable(),
  captured_at: z12.union([z12.string(), z12.date()]).nullable(),
  synced_at: z12.union([z12.string(), z12.date()]).nullable(),
  status: DeliveryProofAssetStatusSchema,
  error: z12.string().nullable(),
  created_at: z12.union([z12.string(), z12.date()]),
  updated_at: z12.union([z12.string(), z12.date()])
});
var DeliveryPartitionProofAssetSchema = z12.object({
  id: z12.string().uuid(),
  partitionId: z12.string().uuid(),
  taskId: z12.string().uuid(),
  onfleetTaskShortId: z12.string().nullable(),
  assetType: DeliveryProofAssetTypeSchema,
  onfleetUploadId: z12.string(),
  sourceUrl: z12.string().nullable(),
  r2Bucket: z12.string().nullable(),
  r2Key: z12.string().nullable(),
  publicUrl: z12.string().nullable(),
  capturedAt: z12.date().nullable(),
  syncedAt: z12.date().nullable(),
  status: DeliveryProofAssetStatusSchema,
  error: z12.string().nullable(),
  createdAt: z12.date(),
  updatedAt: z12.date()
});
var UpsertPendingDeliveryProofAssetInputSchema = z12.object({
  partitionId: z12.string().uuid(),
  taskId: z12.string().uuid(),
  onfleetTaskShortId: z12.string().nullable().optional(),
  assetType: DeliveryProofAssetTypeSchema,
  onfleetUploadId: z12.string().min(1),
  sourceUrl: z12.string().nullable().optional(),
  capturedAt: z12.date().nullable().optional()
});
var MarkDeliveryProofAssetSyncedInputSchema = z12.object({
  sourceUrl: z12.string().nullable().optional(),
  r2Bucket: z12.string(),
  r2Key: z12.string(),
  publicUrl: z12.string(),
  syncedAt: z12.date().optional()
});
var DeliveryPartitionProofSummarySchema = z12.object({
  partitionId: z12.string().uuid(),
  hasSignature: z12.boolean(),
  photoCount: z12.number().int().nonnegative()
});

// delivery/delivery-destination.schema.ts
import { z as z13 } from "zod";
var DeliveryDestinationSchema = z13.object({
  id: z13.string(),
  source: z13.enum(["organization_location", "customer_address"]),
  name: z13.string(),
  address: z13.object({
    address1: z13.string(),
    address2: z13.string().nullable().optional(),
    city: z13.string(),
    province: z13.string(),
    postalCode: z13.string(),
    countryCode: z13.string().optional()
  }),
  complianceLicenseId: z13.string().uuid().nullable().optional(),
  complianceLicenseNumber: z13.string().nullable().optional(),
  organizationLocationId: z13.string().uuid().nullable().optional()
});
var DeliveryDestinationsResponseSchema = z13.object({
  destinations: z13.array(DeliveryDestinationSchema),
  canCreateDestination: z13.boolean()
});
var CreateDeliveryDestinationSchema = z13.object({
  name: z13.string().min(1, "Location name is required"),
  address1: z13.string().min(1, "Street address is required"),
  address2: z13.string().nullable().optional(),
  city: z13.string().min(1, "City is required"),
  province: z13.string().min(1, "State is required"),
  postalCode: z13.string().min(1, "ZIP code is required"),
  locationType: LocationTypeSchema.optional()
});

// delivery/logistics.schema.ts
import { z as z14 } from "zod";
var DerivedShipmentTaskSchema = z14.object({
  onfleetTaskId: z14.string(),
  destinationNodeId: z14.string().uuid().nullable(),
  legId: z14.string().uuid(),
  legSequence: z14.number().int(),
  assignedDriverId: z14.string().uuid().nullable(),
  taskCategory: z14.enum(["partition_delivery", "leg_delivery", "hub_waypoint", "operational"]),
  sortOrder: z14.number().int(),
  /** For partition deliveries — which partition this task belongs to */
  partitionId: z14.string().uuid().nullable(),
  /** For auxiliary tasks — the shipment_auxiliary_tasks row ID */
  auxiliaryTaskId: z14.string().uuid().nullable()
});
var PartitionDiagnosticsSchema = z14.object({
  partitionId: z14.string().uuid(),
  onfleetTaskId: z14.string().nullable(),
  taskExistsInOnfleet: z14.boolean(),
  onfleetTaskState: z14.number().int().nullable(),
  assignedWorker: z14.string().nullable(),
  inRoutePlan: z14.boolean(),
  routePlanId: z14.string().nullable()
});
var ShipmentDiagnosticsSchema = z14.object({
  shipmentId: z14.string().uuid(),
  legCount: z14.number().int(),
  routePlanCount: z14.number().int(),
  partitionTasksExpected: z14.number().int(),
  partitionTasksFound: z14.number().int(),
  auxiliaryTasksCount: z14.number().int(),
  missingOnfleetTasks: z14.array(z14.string().uuid()),
  orphanedRoutePlans: z14.array(z14.string()),
  driverMismatches: z14.array(
    z14.object({
      legId: z14.string().uuid(),
      floteDriver: z14.string(),
      onfleetWorker: z14.string()
    })
  )
});

// delivery/carrier-tracking.types.ts
import { z as z15 } from "zod";
var CarrierTrackingStatusSchema = z15.enum([
  "unknown",
  "pre_transit",
  "in_transit",
  "out_for_delivery",
  "delivered",
  "available_for_pickup",
  "return_to_sender",
  "failure",
  "cancelled"
]);
var CarrierTrackingLocationSchema = z15.object({
  city: z15.string().nullable(),
  state: z15.string().nullable(),
  zip: z15.string().nullable(),
  country: z15.string().nullable()
});
var CarrierTrackingEventSchema = z15.object({
  timestamp: z15.string(),
  status: CarrierTrackingStatusSchema,
  description: z15.string(),
  location: CarrierTrackingLocationSchema.nullable()
});
var CarrierTrackingSnapshotSchema = z15.object({
  partitionId: z15.string(),
  trackingNumber: z15.string(),
  carrier: z15.string(),
  status: CarrierTrackingStatusSchema,
  statusDescription: z15.string(),
  estimatedDeliveryDate: z15.string().nullable(),
  publicTrackingUrl: z15.string().nullable(),
  signedBy: z15.string().nullable(),
  events: z15.array(CarrierTrackingEventSchema),
  lastUpdatedAt: z15.string()
});
export {
  AddHubSplitInputSchema,
  AddHubSplitResultSchema,
  AssignDestinationLicenseInputSchema,
  AssignDestinationLicenseResultSchema,
  AssignmentQueueEntryDtoSchema,
  AssignmentQueueEntrySchema,
  AssignmentQueueFiltersSchema,
  AssignmentQueueStatsSchema,
  AssignmentQueueStatusSchema,
  AttachPartitionsToShipmentLegInputSchema,
  AuxiliaryTaskAddressSchema,
  BatchInsertHubInputSchema,
  BatchInsertHubResultSchema,
  BrandFulfillmentSummarySchema,
  CancelQueueEntryInputSchema,
  CancelQueueEntryResultSchema,
  CarrierTrackingEventSchema,
  CarrierTrackingLocationSchema,
  CarrierTrackingSnapshotSchema,
  CarrierTrackingStatusSchema,
  CreateAssignmentQueueEntrySchema,
  CreateDeliveryDestinationSchema,
  CreateDeliveryShipmentLegAllocationSchema,
  CreateDeliveryShipmentLegManifestSchema,
  CreateDeliveryShipmentLegMetrcRefSchema,
  CreateDeliveryShipmentLegQueueEntrySchema,
  CreateDeliveryShipmentLegSchema,
  CreateDeliveryShipmentManifestSchema,
  CreateDeliveryShipmentPartitionAllocationSchema,
  CreateDeliveryShipmentPlanInputSchema,
  CreateDeliveryShipmentPlanNodeSchema,
  CreateDeliveryShipmentPlanResultSchema,
  CreateDeliveryShipmentSchema,
  CreateFulfillmentOfferDtoSchema,
  CreateFulfillmentRecordDtoSchema,
  CreateOrderPartitionDtoSchema,
  CreateOrderPartitionItemBatchReservationDtoSchema,
  CreateOrderPartitionItemDtoSchema,
  CreateOrderPartitionItemPackageReservationDtoSchema,
  CreateOrderPartitionNoteSchema,
  CreateShipmentAuxiliaryTaskSchema,
  CreateShipmentFromPartitionsInputSchema,
  CreateShipmentFromPartitionsResultSchema,
  CreateShipmentLegTaskVersionSchema,
  DbAssignmentQueueSchema,
  DbDeliveryPartitionProofAssetSchema,
  DbFulfillmentOfferSchema,
  DbFulfillmentRecordSchema,
  DbOrderPartitionItemBatchReservationSchema,
  DbOrderPartitionItemPackageReservationSchema,
  DbOrderPartitionItemSchema,
  DbOrderPartitionNoteSchema,
  DbOrderPartitionSchema,
  DeleteShipmentInputSchema,
  DeleteShipmentLegInputSchema,
  DeleteShipmentLegResultSchema,
  DeleteShipmentResultSchema,
  DeliveryDestinationSchema,
  DeliveryDestinationsResponseSchema,
  DeliveryDownstreamTaskDraftDtoSchema,
  DeliveryDownstreamTaskDraftSchema,
  DeliveryPartitionProofAssetSchema,
  DeliveryPartitionProofSummarySchema,
  DeliveryProofAssetStatusSchema,
  DeliveryProofAssetTypeSchema,
  DeliveryShipmentDtoSchema,
  DeliveryShipmentFiltersSchema,
  DeliveryShipmentLegAllocationDtoSchema,
  DeliveryShipmentLegAllocationFiltersSchema,
  DeliveryShipmentLegAllocationSchema,
  DeliveryShipmentLegAllocationStateSchema,
  DeliveryShipmentLegDtoSchema,
  DeliveryShipmentLegFiltersSchema,
  DeliveryShipmentLegManifestDestinationDtoSchema,
  DeliveryShipmentLegManifestDestinationSchema,
  DeliveryShipmentLegManifestDtoSchema,
  DeliveryShipmentLegManifestFiltersSchema,
  DeliveryShipmentLegManifestFlowSchema,
  DeliveryShipmentLegManifestPackageSchema,
  DeliveryShipmentLegManifestPayloadSnapshotDtoSchema,
  DeliveryShipmentLegManifestPayloadSnapshotSchema,
  DeliveryShipmentLegManifestReadinessDtoSchema,
  DeliveryShipmentLegManifestReadinessIssueSchema,
  DeliveryShipmentLegManifestReadinessSchema,
  DeliveryShipmentLegManifestSchema,
  DeliveryShipmentLegManifestShipperSchema,
  DeliveryShipmentLegManifestStatusSchema,
  DeliveryShipmentLegManifestTransporterSchema,
  DeliveryShipmentLegMetrcRefDtoSchema,
  DeliveryShipmentLegMetrcRefFiltersSchema,
  DeliveryShipmentLegMetrcRefSchema,
  DeliveryShipmentLegPlanAllocationInputSchema,
  DeliveryShipmentLegPlanInputSchema,
  DeliveryShipmentLegQueueEntryDtoSchema,
  DeliveryShipmentLegQueueEntrySchema,
  DeliveryShipmentLegQueueFiltersSchema,
  DeliveryShipmentLegQueueListItemDtoSchema,
  DeliveryShipmentLegQueueListItemSchema,
  DeliveryShipmentLegQueueStatsSchema,
  DeliveryShipmentLegQueueStatusSchema,
  DeliveryShipmentLegQueueSummaryDtoSchema,
  DeliveryShipmentLegQueueSummarySchema,
  DeliveryShipmentLegSchema,
  DeliveryShipmentLegStatusSchema,
  DeliveryShipmentLegTypeSchema,
  DeliveryShipmentManifestDtoSchema,
  DeliveryShipmentManifestFlowSchema,
  DeliveryShipmentManifestReadinessDtoSchema,
  DeliveryShipmentManifestReadinessIssueSchema,
  DeliveryShipmentManifestReadinessSchema,
  DeliveryShipmentManifestSchema,
  DeliveryShipmentManifestStatusSchema,
  DeliveryShipmentPartitionAllocationDtoSchema,
  DeliveryShipmentPartitionAllocationSchema,
  DeliveryShipmentPartitionAllocationStateSchema,
  DeliveryShipmentPlanNodeDtoSchema,
  DeliveryShipmentPlanNodeSchema,
  DeliveryShipmentPlanNodeTypeSchema,
  DeliveryShipmentSchema,
  DeliveryShipmentStatusSchema,
  DeliveryShipmentTimelineItemDtoSchema,
  DeliveryShipmentTimelineItemSchema,
  DerivedShipmentTaskSchema,
  DetachPartitionFromShipmentLegInputSchema,
  DispatchShipmentInputSchema,
  DispatchShipmentPlanSchema,
  DispatchShipmentResultSchema,
  DownstreamDraftPublishStatusSchema,
  EnsureShipmentLegExecutionInputSchema,
  EnsureShipmentLegExecutionResultSchema,
  ExpireOffersResponseDtoSchema,
  FulfillmentOfferDtoSchema,
  FulfillmentOfferFiltersSchema,
  FulfillmentOfferSchema,
  FulfillmentOfferStatusSchema,
  FulfillmentOfferSummarySchema,
  FulfillmentOfferWithPartitionAndItemsDtoSchema,
  FulfillmentOfferWithPartitionDtoSchema,
  FulfillmentRecordDtoSchema,
  FulfillmentRecordFiltersSchema,
  FulfillmentRecordFulfillmentTypeSchema,
  FulfillmentRecordSchema,
  FulfillmentResultSchema,
  GeoJsonLineStringSchema,
  GetDeliveryShipmentDetailInputSchema,
  GetDeliveryShipmentDetailResultSchema,
  GetShipmentLegManifestInputSchema,
  HubLayoverScheduleSchema,
  HubNodeMetadataSchema,
  InsertWaypointInputSchema,
  InsertWaypointResultSchema,
  MarkDeliveryProofAssetSyncedInputSchema,
  NodeAddressSnapshotSchema,
  OfferResponseDtoSchema,
  OnfleetTaskKindSchema,
  OnfleetWebhookTriggers,
  OptimizeShipmentRouteInputSchema,
  OptimizeShipmentRouteResultSchema,
  OptimizeShipmentRouteSegmentSchema,
  OptimizeShipmentRouteSkipReasonSchema,
  OrderPartitionDtoSchema,
  OrderPartitionFiltersSchema,
  OrderPartitionFulfillmentStatusSchema,
  OrderPartitionFulfillmentTypeSchema,
  OrderPartitionItemBatchReservationSchema,
  OrderPartitionItemPackageReservationDtoSchema,
  OrderPartitionItemPackageReservationSchema,
  OrderPartitionItemSchema,
  OrderPartitionItemWithProductSchema,
  OrderPartitionNoteSchema,
  OrderPartitionPaymentStatusSchema,
  OrderPartitionPreparationStatusSchema,
  OrderPartitionSchema,
  POST_DEDUCTION_PREPARATION_STATUSES,
  PaginatedFulfillmentOffersSchema,
  PaginatedOrderPartitionsSchema,
  PartitionDiagnosticsSchema,
  PartitionItemDtoSchema,
  PendingOfferSummarySchema,
  PrepareShipmentLegManifestInputSchema,
  PrepareShipmentLegManifestResultSchema,
  RemoveHubSplitInputSchema,
  RemoveHubSplitResultSchema,
  ReorderStopsInputSchema,
  ReorderStopsResultSchema,
  RespondToOfferDtoSchema,
  ShipmentAuxiliaryTaskCategorySchema,
  ShipmentAuxiliaryTaskSchema,
  ShipmentAuxiliaryTaskStatusSchema,
  ShipmentAuxiliaryTaskTypeSchema,
  ShipmentDiagnosticsSchema,
  ShipmentLegExecutionStrategySchema,
  ShipmentLegMetrcBackfillInputSchema,
  ShipmentLegMetrcBackfillMetricsSchema,
  ShipmentLegMetrcBackfillPartitionQuerySchema,
  ShipmentLegMetrcBackfillRecordCategorySchema,
  ShipmentLegMetrcBackfillRecordSchema,
  ShipmentLegMetrcBackfillReportSchema,
  ShipmentLegMetrcLinkInputSchema,
  ShipmentLegMetrcReconciliationInputSchema,
  ShipmentLegMetrcReconciliationIssueSchema,
  ShipmentLegMetrcReconciliationIssueTypeSchema,
  ShipmentLegMetrcReconciliationMetricsSchema,
  ShipmentLegMetrcReconciliationReportSchema,
  ShipmentLegMetrcRefFieldSubsetSchema,
  ShipmentLegMetrcRefSourceSchema,
  ShipmentLegTaskVersionSchema,
  ShipmentManifestDestinationSchema,
  ShipmentManifestPackageSchema,
  ShipmentManifestPayloadSnapshotSchema,
  ShipmentManifestShipperSchema,
  ShipmentManifestTransporterDetailSchema,
  ShipmentManifestTransporterSchema,
  StalePartitionFiltersSchema,
  StockLocationPerformanceSchema,
  SubmitShipmentLegManifestInputSchema,
  TaskReplaceReasonSchema,
  UpdateAssignmentQueueEntrySchema,
  UpdateDeliveryShipmentLegAllocationSchema,
  UpdateDeliveryShipmentLegManifestSchema,
  UpdateDeliveryShipmentLegMetrcRefSchema,
  UpdateDeliveryShipmentLegQueueEntrySchema,
  UpdateDeliveryShipmentLegSchema,
  UpdateDeliveryShipmentManifestSchema,
  UpdateDeliveryShipmentPartitionAllocationSchema,
  UpdateDeliveryShipmentPlanNodeSchema,
  UpdateDeliveryShipmentSchema,
  UpdateHubMetadataInputSchema,
  UpdateHubMetadataResultSchema,
  UpdateOrderPartitionDtoSchema,
  UpdateOrderPartitionNoteSchema,
  UpdateShipmentAuxiliaryTaskSchema,
  UpdateShipmentLegInputSchema,
  UpdateShipmentLegManifestInputSchema,
  UpdateShipmentLegResultSchema,
  UpsertPendingDeliveryProofAssetInputSchema,
  VoidShipmentLegManifestInputSchema,
  isPostDeductionPreparationStatus
};
